/**
 * Typed service layer over `@erpat/sdk`.
 *
 * One object per API namespace. Read methods return mapped view models (tab
 * domains) or `{ items, pagination }` / raw `data` (everything else); write
 * methods return the raw `data`. Every call goes through {@link withAuthRetry}
 * via `unwrap` / `unwrapList`, so a 401 transparently refreshes the token once.
 * The exception is a call whose body carries a credential (password or Security
 * PIN): those use `withCredentialRetry`, which refreshes only on a TOKEN 401 —
 * a wrong PIN or password is an answer, not an expired session.
 *
 * Request bodies use the server's snake_case field names. These are the best
 * mapping from each page's form; confirm against the endpoint catalog and
 * adjust the field names here (call sites stay unchanged).
 */
import { ErpatApiError, type ListQuery, type QueryValue } from '@erpat/sdk';
import { erpatClient, getStoredCompanyKey, getSessionUserId, sessionFetch } from '@/services/api/erpat-client';
import { API_BASE_URL, ERPAT_APP_SLUG, ERPAT_APP_DASHBOARD_KEY, IS_DEV } from '@/utils/env';
import { isSecurityChallenge } from '@/utils/edge-challenge';
import {
  unwrap,
  unwrapList,
  withAuthRetry,
  withCredentialRetry,
  toList,
  toPagination,
  pick,
  asRecord,
  domainBody,
  type Json,
  type Pagination,
} from '@/services/api/http';
import {
  fetchBootstrap as fetchRealtimeBootstrap,
  sendTest as sendRealtimeTest,
  type RealtimeBootstrap,
} from '@/services/api/realtime.api';
import {
  streamAssistant,
  type AssistantStreamHandlers,
  type AssistantStreamResult,
} from '@/services/api/assistant-stream';
import { APP_VERSION, detectPlatform } from '@/utils/app-info';
import { currentWeekDates, localDateKey } from '@/utils/format';
import { addCivilDays } from '@/services/api/calendar';
import { labelIdFor } from '@/config/erpat-labels';
import {
  mapRealtimeLogFilters,
  mapRealtimeLogsPage,
  realtimeLogsQueryParams,
  type RealtimeLogFiltersVM,
  type RealtimeLogsPage,
  type RealtimeLogsQuery,
} from '@/services/api/biotime-realtime';
import { parseScene, type SketchScene } from '@/utils/sketch-scene';
import { isUuidV4 } from '@/utils/uuid';
import {
  mapDashboard,
  mapAppDashboard,
  mapBirthdayCelebrants,
  mapAnniversaryCelebrants,
  mapMoodToday,
  mapMoodHistory,
  mapTodayAttendance,
  mapWeekSchedule,
  mapCutoffPeriod,
  mapHolidayDays,
  mapLeaveDays,
  mapLeaveApplicationsByDay,
  type DayLeaveVM,
  mapTeamAttendanceRecord,
  readTeamAttendanceListMeta,
  type TeamAttendanceListMeta,
  mapAttendanceScheduleOptions,
  type AttendanceEditBody,
  type AttendanceScheduleOptionVM,
  mapTeamLeaveRecord,
  mapLeaveReviewDepartments,
  mapCapabilities,
  mapLeaveBalances,
  mapLeaveFilingRules,
  mapProfile,
  mapPreferences,
  mapAssistantConversation,
  mapAssistantConversationList,
  mapAssistantReply,
  mapAssistantSuggestions,
  mapConversation,
  mapConversationList,
  mapGroupMembers,
  mapContacts,
  mapNotifications,
  mapNotificationListWithCount,
  mapPayslipListItem,
  mapPayslipDetail,
  mapPayslipTotals,
  mapLeaveRequests,
  mapLeaveDetail,
  mapLeaveAttachment,
  mapLeaveAttachmentLink,
  mapAdvisory,
  mapAdvisoryFileLink,
  mapKbArticles,
  mapHandbookSummary,
  mapHandbookArticle,
  mapTickets,
  mapTicket,
  mapTicketReplyResult,
  mapTicketFormOptions,
  mapTicketPeople,
  mapTicketMentionables,
  mapTicketTemplates,
  mapTicketKbOptions,
  mapTicketFileLink,
  mapSupportChannels,
  mapNotes,
  mapNoteDetail,
  mapTimelinePosts,
  mapTimelinePostDetail,
  mapTimelineReactionResponse,
  mapDisciplinary,
  mapDisciplinaryDetail,
  mapDepartment,
  mapForms,
  mapFormDetail,
  mapSurveyQueue,
  mapActivityEntry,
  mapActivitySummary,
  type ActivityEntryVM,
  type ActivitySummaryVM,
  type DashboardVM,
  type AppDashboardVM,
  type TodayAttendanceVM,
  type TeamAttendanceVM,
  type TeamAttendanceAction,
  type AttendanceOtDecision,
  type AttendanceLunchChoice,
  type TeamLeaveVM,
  type TeamLeaveAction,
  type LeaveReviewDepartmentVM,
  type WeekScheduleVM,
  type CutoffPeriodVM,
  type CapabilitiesVM,
  type LeaveBalanceVM,
  type LeaveFilingRulesVM,
  type LeaveRequestVM,
  type LeaveDetailVM,
  type LeaveAttachmentVM,
  type LeaveAttachmentLinkVM,
  type ProfileVM,
  type PreferencesVM,
  type AssistantConversationVM,
  type AssistantMessageVM,
  type AssistantSuggestionVM,
  type PayslipListItemVM,
  type PayslipDetailVM,
  type PayslipTotalsVM,
  mapSalaryRecord,
  type SalaryRecordVM,
  mapSecurityPinStatus,
  type SecurityPinStatusVM,
  type AdvisoryVM,
  type KbArticleVM,
  type HandbookSummaryVM,
  type HandbookArticleVM,
  type SupportChannelVM,
  type NoteVM,
  type NoteDetailVM,
  type NoteType,
  type TimelinePostVM,
  type TimelinePostDetailVM,
  type TimelineReactionsVM,
  type DisciplinaryCaseVM,
  type DisciplinaryCaseDetailVM,
  type DepartmentVM,
  type FormSummaryVM,
  type FormDetailVM,
  type SurveyPromptVM,
  type CelebrationsVM,
  type AnniversaryCelebrantVM,
  type MoodTodayVM,
  type MoodEntryVM,
  mapLoanList,
  mapLoanDetail,
  mapLoanPayments,
  mapLoanSchedule,
  type LoanVM,
  type LoanDetailVM,
  type LoanPaymentsVM,
  type LoanScheduleVM,
  mapAssetCategories,
  mapAssets,
  mapAssetDetail,
  mapAssetIssues,
  mapReportIssueResult,
  mapEndorsements,
  mapEndorsementDetail,
  mapCaretakerAssets,
  // Projects
  mapProjects,
  mapProjectDetail,
  mapProjectTasks,
  mapProjectTask,
  mapProjectTaskDetail,
  mapProjectTaskStatuses,
  mapProjectChecklistItem,
  mapProjectMilestones,
  mapProjectMembers,
  mapProjectComments,
  mapProjectComment,
  mapProjectTimeLogs,
  mapProjectTimers,
  mapProjectTimer,
} from '@/services/api/models';
import type {
  AssetCategoryVM,
  AssetVM,
  AssetDetailVM,
  AssetFieldSchemaField,
  AssetIssueVM,
  AssetEndorsementVM,
  AssetEndorsementDetailVM,
  CaretakerAssetVM,
  ReportIssueInput,
  ReportIssueResultVM,
  AcceptEndorsementInput,
} from '@/types/asset';
import type {
  ProjectStatus,
  ProjectVM,
  ProjectDetailVM,
  ProjectTaskVM,
  ProjectTaskDetailVM,
  ProjectTaskStatusVM,
  ProjectChecklistItemVM,
  ProjectMilestoneVM,
  ProjectMemberVM,
  ProjectCommentVM,
  ProjectTimeLogVM,
  ProjectTimerVM,
  ProjectTimersVM,
} from '@/types/project';
import type { Conversation, Contact, GroupMember } from '@/types/message';
import type { NotificationItem } from '@/types/notification';
import type {
  Ticket,
  TicketRawStatus,
  TicketFormOptions,
  TicketPerson,
  TicketMentionable,
  TicketTemplateOption,
  TicketKbOption,
} from '@/types/ticket';
import type { NotificationListWithCount, TicketReplyResultVM } from '@/services/api/models';
import {
  mapEvents,
  mapEventDetail,
  mapAttendees,
  mapRsvpResult,
  mapReminderLead,
} from '@/services/api/events';
import type {
  AttendeeVM,
  EventDetailVM,
  EventVM,
  ReminderLead,
  RsvpResultVM,
  RsvpStatus,
} from '@/types/event';
import {
  correctiveActionListParams,
  investigationListParams,
  mapCorrectiveActionDetail,
  mapCorrectiveActionPage,
  mapInvestigationDetail,
  mapInvestigationPage,
} from '@/services/api/compliance';
import type {
  CorrectiveActionDetail,
  CorrectiveActionPatch,
  CorrectiveActionQuery,
  CorrectiveActionVM,
  DisciplinaryReply,
  InvestigationDetail,
  InvestigationPatch,
  InvestigationQuery,
  InvestigationVM,
} from '@/types/compliance';

export type Paged<T> = { items: T[]; pagination: Pagination };

// ── System (health probe) ────────────────────────────────────────────────────
/**
 * What the health probe found. `blocked` is deliberately not `offline`: a
 * security check in front of the server answered in its place, so the network
 * is fine and neither waiting nor retrying helps — an administrator has to let
 * the app through.
 */
export type ServerProbe = 'online' | 'offline' | 'blocked';

export const systemService = {
  /**
   * GET /v1/api/ping — the API's unauthenticated health probe. Resolves
   * `online` when the server answers, `blocked` when an edge's security check
   * intercepted the request (see utils/edge-challenge.ts), and `offline` for
   * any other transport/HTTP failure.
   *
   * Deliberately bypasses `unwrap()`: a probe must never run the 401
   * refresh-retry or tear a session down, and the payload is irrelevant — only
   * reachability matters. An aborted probe rejects (rather than reporting a
   * false "offline") so callers can drop a stale result on unmount.
   */
  probe: async (signal?: AbortSignal): Promise<ServerProbe> => {
    try {
      await erpatClient.system.ping(signal);
      return 'online';
    } catch (err) {
      if (signal?.aborted) throw err;
      return isSecurityChallenge(err) ? 'blocked' : 'offline';
    }
  },

  /**
   * {@link systemService.probe} as a yes/no — `true` only when the server
   * itself answered. Same contract: an aborted probe rejects.
   */
  ping: (signal?: AbortSignal): Promise<boolean> =>
    systemService.probe(signal).then((state) => state === 'online'),
};

// ── Dashboard (Home tab) ─────────────────────────────────────────────────────
export const dashboardService = {
  get: (signal?: AbortSignal): Promise<DashboardVM> =>
    unwrap(() => erpatClient.dashboard.get(signal)).then(mapDashboard),
};

// ── App Dash (server-configured Home layout) ─────────────────────────────────
export const appDashboardService = {
  /**
   * GET /v1/api/me/app-dashboard?app=<slug>[&key=<dashboard_key>] →
   * `{ dashboard: {...} | null }` — the tenant's PUBLISHED App Dash layout for
   * THIS app, or `null` when none is published (callers keep the built-in home
   * layout). The endpoint isn't in `@erpat/sdk` yet, so it goes through the
   * low-level `request()` escape hatch (same pattern as `teamAttendanceService`).
   *
   * WHICH dashboard this resolves to, end to end:
   *   `app`  — {@link ERPAT_APP_SLUG}, default `syntry` = the App Library entry
   *            named "Syntry App". The server matches the SLUG and 422s an
   *            unknown one, so this is never guessed.
   *   `key`  — {@link ERPAT_APP_DASHBOARD_KEY}, normally empty. Omitted, the
   *            server returns the tenant's default published dashboard
   *            (`is_default DESC, sort_order ASC, name ASC`); supplied, it
   *            returns only that dashboard_key, still published-only.
   *
   * `key` is omitted from the query entirely when unset — sending `key=''` would
   * filter on an empty dashboard_key and always resolve to `dashboard: null`.
   */
  layout: (signal?: AbortSignal): Promise<AppDashboardVM | null> =>
    unwrap(() =>
      erpatClient.request('/v1/api/me/app-dashboard', {
        query: ERPAT_APP_DASHBOARD_KEY
          ? { app: ERPAT_APP_SLUG, key: ERPAT_APP_DASHBOARD_KEY }
          : { app: ERPAT_APP_SLUG },
        signal,
      }),
    ).then(mapAppDashboard),
};

// ── Attendance + schedule (Activity tab) ─────────────────────────────────────
export interface ClockPayload {
  /** Optional free-text note — the only field the clock-in/out contract defines. */
  note?: string;
  [field: string]: unknown;
}
/** Merge attendance/today (clock state) with schedule/today (shift) into one VM. */
export async function loadToday(signal?: AbortSignal): Promise<TodayAttendanceVM> {
  const [att, sched] = await Promise.all([
    unwrap(() => erpatClient.attendance.today(signal)),
    unwrap(() => erpatClient.schedule.today(signal)).catch(() => null),
  ]);
  return mapTodayAttendance(att, sched);
}

/**
 * Whether this backend has `GET /me/attendance/summary`, probed once per page
 * load — see `attendanceService.summaryIfAvailable`.
 */
let summaryRoute: 'unknown' | 'present' | 'absent' = 'unknown';

export const attendanceService = {
  today: (signal?: AbortSignal): Promise<TodayAttendanceVM> => loadToday(signal),
  /**
   * The RAW `/me/attendance/today` body, for derivations that need the punch's
   * real stamps rather than the VM's display labels.
   *
   * WHY BOTH EXIST. `today()` maps this into `TodayAttendanceVM`, whose
   * `clockInLabel` is already formatted ("8:00 AM") and has therefore LOST the
   * calendar date. That matters because `open_record` is the shift the employee
   * is on *right now*, whichever day it started — an overnight shift, or a punch
   * nobody ever clocked out of. A windowed `list()` will not return such a row
   * for today, so anything deriving "today's" numbers from the list alone
   * measures nothing while the rest of the UI happily shows that same open
   * shift. See `loadTodayMetrics` in services/widgets.ts.
   */
  todayRaw: (signal?: AbortSignal): Promise<Json> =>
    unwrap(() => erpatClient.attendance.today(signal)) as Promise<Json>,
  list: (query?: ListQuery, signal?: AbortSignal): Promise<Paged<Json>> =>
    unwrapList(() => erpatClient.attendance.list(query, signal)),
  /**
   * GET /v1/api/me/attendance/summary — the caller's absences (DAYS, in half-day
   * steps: 0.5 = one half of a shift missed), tardiness and — from approved
   * attendance only, per day with `metrics_approved` — undertime and overbreak
   * (whole MINUTES) over a
   * local window, computed server-side with the Shift Planner, holidays and
   * approved paid leave applied. Both bounds are optional (server default: the
   * trailing 15 days; a future end is clamped to today; max 62 days).
   *
   * Not in `@erpat/sdk` yet, so it goes through `request()`. Returns the raw
   * `summary` body — `services/widgets.ts` maps it. A backend without the route
   * answers 404/405; callers treat that as "not available" and fall back.
   */
  summary: (query?: { start_date?: string; end_date?: string }, signal?: AbortSignal): Promise<Json> =>
    unwrap(() =>
      erpatClient.request('/v1/api/me/attendance/summary', {
        query: query as Record<string, QueryValue> | undefined,
        signal,
      }),
    ).then((raw) => domainBody(raw, ['summary'])),
  /**
   * {@link summary}, or `null` when this backend predates the route.
   *
   * The ONE place that decides whether the route exists, shared by the Home
   * pay-period widgets and the Timesheet / Reports cut-off, so they can never
   * run different formulas for the same period. A 404/405 for the ROUTE marks it
   * absent for the rest of the page load. Every other failure is re-thrown —
   * including a `module_unavailable` 404 (the HR module is off, and the
   * fallback's own endpoints would be refused the same way) and anything after
   * the route has already answered once.
   */
  summaryIfAvailable: async (
    query: { start_date: string; end_date: string },
    signal?: AbortSignal,
  ): Promise<Json | null> => {
    if (summaryRoute === 'absent') return null;
    try {
      const summary = await attendanceService.summary(query, signal);
      summaryRoute = 'present';
      return summary;
    } catch (err) {
      const missingRoute =
        err instanceof ErpatApiError &&
        (err.status === 404 || err.status === 405) &&
        err.code !== 'module_unavailable';
      if (missingRoute && summaryRoute !== 'present') {
        summaryRoute = 'absent';
        return null;
      }
      throw err;
    }
  },
  get: (id: string, signal?: AbortSignal): Promise<Json> =>
    unwrap(() => erpatClient.attendance.get(id, signal)) as Promise<Json>,
  // The punch POSTs answer ERPat's own body (`receipt`) and NOTHING else: the
  // caller refreshes today itself, separately (the attendance store does it
  // fire-and-forget). Chaining the GET here turned a failed follow-up read into
  // an error for a punch ERPat had already committed (offline-attendance D9).
  clockIn: async (body: ClockPayload = {}): Promise<{ receipt: unknown }> => ({
    receipt: await unwrap(() => erpatClient.attendance.clockIn(body)),
  }),
  clockOut: async (body: ClockPayload = {}): Promise<{ receipt: unknown }> => ({
    receipt: await unwrap(() => erpatClient.attendance.clockOut(body)),
  }),
  break: async (action: 'start' | 'end'): Promise<{ receipt: unknown }> => ({
    // POST /me/attendance/break expects { type } (break in/out hint), not { action } — verified against openapi.json.
    receipt: await unwrap(() => erpatClient.attendance.break({ type: action })),
  }),
};

/**
 * Manager-level attendance — OTHER employees' records, with an approval flow.
 *
 * These three endpoints are NOT in `@erpat/sdk` yet, so we call them through
 * the SDK's `request()` escape hatch (the same pattern as `messageGroups.members`):
 *   GET   /v1/api/attendance              — list rows the caller may see
 *   GET   /v1/api/attendance/{id}         — one detailed row
 *   PATCH /v1/api/attendance/{id}/status  — approve / reject / revert-to-pending
 *
 * Reads require the `attendance` permit (RBAC, row-scoped: all|specific);
 * `updateStatus` additionally requires `attendance_approval`. Out-of-scope ids
 * return 404 (existence-hiding), so the page treats 403/404 as "not available".
 * Verified against `v1/api/hr/TeamAttendance.php`; the published openapi.json
 * documents only the envelope for these routes (no body/response shape).
 */
export interface TeamAttendanceQuery {
  page?: number;
  per_page?: number;
  /** Narrow to a single employee (still bounded by the caller's row-scope). */
  user_id?: number;
  /** Filter: incomplete | pending | approved | rejected | clockout. */
  status?: string;
  /** Inclusive local-date window (YYYY-MM-DD). Defaults: end=today, start=end-31d. */
  start_date?: string;
  end_date?: string;
}

/** One page of the team attendance list, plus what the server says about the caller. */
export interface TeamAttendancePage extends Paged<TeamAttendanceVM> {
  meta: TeamAttendanceListMeta;
}

/**
 * The tenant's payroll cut-off calendar — ERPat web → HR Settings → Payroll Cut-off.
 *
 * WHY THIS EXISTS. `utils/format.ts semiMonthlyCutoff()` derives the 1st–15th / 16th–EOM split
 * ON THE DEVICE. That was the only option when it was written, and its docblock says so — no
 * endpoint exposed the boundaries of a period still in progress. It is also simply WRONG for any
 * tenant that does not run 1–15 / 16–EOM: a real company on this build pays 11–25 and 26–10, so
 * the "Current Cut Off" button showed dates their payroll never uses.
 *
 * `GET /v1/api/me/payroll-cutoffs` answers the real boundaries for the CALLING employee
 * (a department can be assigned its own calendar). The dates are computed server-side from the
 * same rule the payroll is built from, so the app never has to guess — and cannot drift.
 *
 * A tenant with no calendar configured answers 200 with nulls and empty arrays. That is a normal
 * state, not an error: callers keep the device-derived fallback for it.
 */
export interface CutoffPeriodDTO {
  key: string;
  label: string;
  start_date: string;
  end_date: string;
  is_current?: boolean;
}

export interface CutoffCalendarDTO {
  schedule: { id: number; name: string; period_type: string } | null;
  current: CutoffPeriodDTO | null;
  previous: CutoffPeriodDTO | null;
  periods: CutoffPeriodDTO[];
}

export const payrollCutoffService = {
  mine: (signal?: AbortSignal): Promise<CutoffCalendarDTO> =>
    unwrap(() => erpatClient.request('/v1/api/me/payroll-cutoffs', { signal })).then((raw) => {
      const body = (raw ?? {}) as Record<string, unknown>;
      const period = (v: unknown): CutoffPeriodDTO | null => {
        const p = v as Record<string, unknown> | null;
        // Both bounds are required: a half-bounded window would turn into an open-ended
        // attendance query, which is worse than falling back to the device-derived period.
        if (!p || typeof p.start_date !== 'string' || typeof p.end_date !== 'string') return null;
        return {
          key: typeof p.key === 'string' ? p.key : `${p.start_date}_${p.end_date}`,
          label: typeof p.label === 'string' ? p.label : '',
          start_date: p.start_date,
          end_date: p.end_date,
          is_current: p.is_current === true,
        };
      };
      return {
        schedule: (body.schedule as CutoffCalendarDTO['schedule']) ?? null,
        current: period(body.current),
        previous: period(body.previous),
        periods: Array.isArray(body.periods)
          ? (body.periods.map(period).filter(Boolean) as CutoffPeriodDTO[])
          : [],
      };
    }),
};

/**
 * Whether `GET /v1/api/me/cutoff-earnings` exists on this backend — decided once per page
 * load, exactly as `summaryRoute` is for the attendance summary.
 */
let cutoffEarningsRoute: 'unknown' | 'present' | 'absent' = 'unknown';

/**
 * What the caller's pay period is worth — ERPat web → the payroll engine, priced for ONE
 * employee over ONE cut-off without a pay run existing.
 *
 * WHY THIS EXISTS. `services/widgets.ts` used to price the Cutoff Earnings card on the
 * device: half the monthly salary whatever the cut-off length, absences × (monthly × 12 ÷
 * 261), no allowances, no deductions. Every one of those is a rule payroll already owns.
 * `GET /v1/api/me/cutoff-earnings` runs the SAME calls a payroll run makes — the attendance
 * aggregation, the earning / deduction template engine, the loan block and the pay engine's
 * base-pay methods — twice over the period: at full attendance (`entitled`, the card's Gross
 * Earnings) and on attendance so far with the remaining days assumed complete (`projected`,
 * the card's Projected Take Home). The app displays; it no longer computes.
 *
 * Send the WHOLE period (`start_date` / `end_date` from a `/me/payroll-cutoffs` period, up
 * to 62 days); the server decides what has elapsed. A period whose payslip is already
 * released answers `estimate_source: "payslip"` with the stored lines. `has_salary: false`
 * means no approved salary rate — every money figure is 0 and the card says so.
 *
 * Errors: 404 `module_unavailable` (Compensation or HR off), 403, 422 `validation_failed`,
 * 500 `estimate_failed`. See `docs/openapi.json` → `v1.api.me.cutoff_earnings.show`.
 */
export const cutoffEarningsService = {
  estimate: (query: { start_date: string; end_date: string }, signal?: AbortSignal): Promise<Json> =>
    unwrap(() =>
      erpatClient.request('/v1/api/me/cutoff-earnings', {
        query: query as Record<string, QueryValue>,
        signal,
      }),
    ).then((raw) => domainBody(raw, ['cutoff_earnings'])),
  /**
   * {@link estimate}, or `null` when this backend predates the route.
   *
   * The ONE place that decides whether the route exists. A 404/405 for the ROUTE marks it
   * absent for the rest of the page load and the caller keeps its device-side formula.
   * Every other failure is re-thrown — including a `module_unavailable` 404 and a 403,
   * which the caller maps to "salary information isn't available", and anything after the
   * route has already answered once.
   */
  estimateIfAvailable: async (
    query: { start_date: string; end_date: string },
    signal?: AbortSignal,
  ): Promise<Json | null> => {
    if (cutoffEarningsRoute === 'absent') return null;
    try {
      const estimate = await cutoffEarningsService.estimate(query, signal);
      cutoffEarningsRoute = 'present';
      return estimate;
    } catch (err) {
      const missingRoute =
        err instanceof ErpatApiError &&
        (err.status === 404 || err.status === 405) &&
        err.code !== 'module_unavailable';
      if (missingRoute && cutoffEarningsRoute !== 'present') {
        cutoffEarningsRoute = 'absent';
        return null;
      }
      throw err;
    }
  },
};

export const teamAttendanceService = {
  /**
   * One page of the attendance the caller may manage, PLUS what the server says
   * about the caller (`meta.can_self_manage` — whether they may decide their own
   * records, which are always in the list).
   *
   * Shaped here rather than through `unwrapList`, as `teamLeavesService.list()`
   * is: `unwrapList` returns only `{ items, pagination }`. The endpoint nests its
   * `meta` inside `data`, which is where `unwrapList` would have read it from.
   */
  list: async (query?: TeamAttendanceQuery, signal?: AbortSignal): Promise<TeamAttendancePage> => {
    const data = await unwrap(() =>
      erpatClient.request('/v1/api/attendance', { query: query as Record<string, QueryValue>, signal }),
    );
    const rows = toList<Json>(data);
    const meta = asRecord(pick<unknown>(asRecord(data), ['meta'], {}));

    return {
      items: rows.map((row, i) => mapTeamAttendanceRecord(row, i)),
      pagination: toPagination(meta, rows.length),
      meta: readTeamAttendanceListMeta(meta),
    };
  },

  /**
   * One record, as the web approval window sees it. This is the approval sheet's
   * source of truth, not the list row: for a PENDING record the server computes
   * the metrics LIVE through the same engine the web uses (`metrics_source:
   * "computed"`) and attaches the `approval` gate — neither of which the list
   * carries, because computing them is 2–3 queries plus an engine run per row.
   */
  get: (id: string, signal?: AbortSignal): Promise<TeamAttendanceVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/attendance/${encodeURIComponent(id)}`, { signal }),
    ).then(mapTeamAttendanceRecord),

  /**
   * Approve / reject / revert an attendance row. The backend recomputes payroll
   * metrics on approval. Returns the freshly-shaped, mapped record.
   *
   * THE OVERTIME DECISION (`otDecision`) is one of four states, and the last two
   * are different answers even though both hand the hours to the tenant's
   * standing policy:
   *
   *   'approved' — authorise the measured overtime hours.
   *   'rejected' — REJECT them. A real, attributed decision recorded against
   *                this approver (reversible in the web Overtime tab).
   *   'policy'   — the approver CHOSE to let the standing `overtime_auto_policy`
   *                decide. This is what a batch sends: the web's bulk modal
   *                defaults to exactly this, and it is never refused.
   *   undefined  — the approver never answered. While the tenant has
   *                `overtime_approval` on, the record is pending, it measures
   *                overtime for the posted lunch choice and the caller can decide
   *                it, the server REFUSES this with 409 `overtime_decision_required`
   *                — the same rule the web window enforces.
   *
   * Never collapse undefined into 'rejected': that stamped every plain approval
   * as an overtime rejection in the approver's name, which is the failure the
   * backend's own contract note calls out.
   *
   * `logNoLunch` is sent only when the approver actually chose — "Skip Lunch
   * Break" recomputes without the lunch deduction and can put a day into overtime
   * the opening figures never showed, so it is never a silent default. It needs
   * `attendance_update`; the server answers 403 otherwise rather than quietly
   * storing different hours.
   */
  updateStatus: (
    id: string,
    status: TeamAttendanceAction,
    options: TeamAttendanceStatusOptions = {},
  ): Promise<TeamAttendanceVM> => {
    const body: Record<string, unknown> = { status };
    if (options.otDecision) body.ot_decision = options.otDecision;
    if (options.logNoLunch) body.log_no_lunch = options.logNoLunch;
    return unwrap(() =>
      erpatClient.request(`/v1/api/attendance/${encodeURIComponent(id)}/status`, {
        method: 'PATCH',
        body,
      }),
    ).then(mapTeamAttendanceRecord);
  },

  /**
   * Correct a record's time logs — clock-in, clock-out, breaks, schedule, note —
   * through ERPat's own "Edit Time Logs" rule set (`PATCH /v1/api/attendance/{id}`).
   * Build the body with `buildAttendanceEditBody()`: times are company-LOCAL
   * strings, the note travels only when it changed, and untouched times keep
   * their seconds. The server reopens the record as `pending` and answers with
   * the full DETAIL (live metrics + approval gate), so the sheet can show the
   * corrected figures straight away.
   */
  edit: (id: string, body: AttendanceEditBody): Promise<TeamAttendanceVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/attendance/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: { ...body },
      }),
    ).then(mapTeamAttendanceRecord),

  /** The schedule templates a correction may assign (the web edit form's picker). */
  schedules: (signal?: AbortSignal): Promise<AttendanceScheduleOptionVM[]> =>
    unwrap(() => erpatClient.request('/v1/api/attendance/schedules', { signal })).then(
      mapAttendanceScheduleOptions,
    ),
};

/**
 * BioTime Live Timelogs — raw biometric punches read through ERPat straight from
 * the company's BioTime server (`modules/Biotime/specs/biotime-realtime-logs-contract.md`
 * §8). Reached through `erpatClient.request()`, the same escape hatch as
 * `teamAttendanceService`: @erpat/sdk has no namespace for these routes.
 *
 * Gated server-side on `module_biotime` + `module_human_resource` + the
 * `attendance` permit (the ATTENDANCE authority — the same permit and row scope
 * as `teamAttendanceService`, Biotime 1.11.0) and a configured connection; the
 * rows, punches, pickers and totals never exceed the employees that authority
 * lets the caller see, and every answer carries an `access` block naming the
 * scope. The client asks, and lets a 403 / 404 answer
 * (`services/realtime-logs.ts` → `realtimeLogsAvailability()`).
 *
 * NOTHING HERE IS CACHED on the device (§9 "Device cache"): `/v1/api/biotime/*`
 * sits outside every service-worker caching route, and neither method stores a
 * row, a name or a code.
 */
export const biotimeRealtimeService = {
  /**
   * One page of employee-days (`GET /v1/api/biotime/live-timelogs`, §8.1/§8.2).
   * Always asks for `include=punches` — the detail sheet reads the raw punches
   * off the row, with no second request. Empty filters are omitted, never sent
   * as `""`.
   *
   * A 202 is a SUCCESS here, not an error: the SDK resolves every 2xx
   * (`response.ok`), so the answer arrives as an ordinary `ApiResult` with
   * `status: 202`, an empty `live_timelogs` and `meta.complete: false`. That is
   * why this calls `withAuthRetry()` rather than `unwrap()` — `unwrap()` keeps
   * only `data` and would drop the status and the `Retry-After` header the SDK
   * parsed into `rateLimit.retryAfter`. `meta.retry_after` in the body is the
   * authority; the header is read only when the body is silent (and a
   * cross-origin browser sees it only if ERPat exposes it in
   * `Access-Control-Expose-Headers`, which is not guaranteed). Waiting and
   * asking again is the caller's job: `loadRealtimeLogsPage()`.
   */
  list: async (query: RealtimeLogsQuery = {}, signal?: AbortSignal): Promise<RealtimeLogsPage> => {
    const res = await withAuthRetry(() =>
      erpatClient.request('/v1/api/biotime/live-timelogs', {
        query: realtimeLogsQueryParams(query),
        signal,
      }),
    );
    return mapRealtimeLogsPage(res.data, {
      status: res.status,
      envelopeMeta: res.meta,
      retryAfterHeader: res.rateLimit?.retryAfter ?? null,
    });
  },

  /**
   * The pickers' options (`GET /v1/api/biotime/live-timelogs/filters`, §8.5) —
   * and the availability probe: a 200 means the report may be offered, a 403 /
   * 404 that it may not. Reads ERPat's synced lists only; it never reaches
   * BioTime.
   */
  filters: (signal?: AbortSignal): Promise<RealtimeLogFiltersVM> =>
    unwrap(() => erpatClient.request('/v1/api/biotime/live-timelogs/filters', { signal })).then(
      mapRealtimeLogFilters,
    ),
};

/** Fields a status change may carry alongside the status itself. */
export interface TeamAttendanceStatusOptions {
  /** See {@link teamAttendanceService.updateStatus} — omitted means "never answered". */
  otDecision?: AttendanceOtDecision;
  /** Omitted unless the approver could, and did, choose. */
  logNoLunch?: AttendanceLunchChoice;
}

/** Rows per page — the ceiling `/me/attendance` enforces (`Attendance_euapi::$maxPerPage`). */
const ATTENDANCE_PAGE_SIZE = 100;

/**
 * Hard stop on pages read for one span. A payroll cut-off of biometric taps runs
 * to a few hundred rows; this only guards against a server that never reports a
 * last page.
 */
const ATTENDANCE_MAX_PAGES = 20;

/**
 * EVERY `/me/attendance` row whose punch-in falls in [startKey, endKey].
 *
 * Paged, because the route caps a page at {@link ATTENDANCE_PAGE_SIZE} rows,
 * newest first. A single request (which asked for 200 and silently got 100)
 * dropped the OLDEST days of a busy cut-off — biometric taps and split shifts put
 * well over 100 rows in one — and those days then read as absent.
 *
 * Rows are de-duplicated by id, and a page that adds nothing new ends the read,
 * so a server that ignored `page` can never double-count punches.
 */
async function attendanceRowsIn(startKey: string, endKey: string, signal?: AbortSignal): Promise<Json[]> {
  const rows: Json[] = [];
  const seen = new Set<string>();
  for (let page = 1; page <= ATTENDANCE_MAX_PAGES; page += 1) {
    const { items } = await unwrapList(() =>
      erpatClient.attendance.list(
        { start_date: startKey, end_date: endKey, page, per_page: ATTENDANCE_PAGE_SIZE },
        signal,
      ),
    );
    let added = 0;
    for (const item of items) {
      const id = item.id == null ? '' : String(item.id);
      if (id !== '' && seen.has(id)) continue;
      if (id !== '') seen.add(id);
      rows.push(item);
      added += 1;
    }
    if (items.length < ATTENDANCE_PAGE_SIZE || added === 0) break;
  }
  return rows;
}

/**
 * The caller's non-working holidays across a span, date → title — see
 * `mapHolidayDays` for the rule. Never rejects: a failed holiday list marks no
 * day a holiday (the schedule still renders), and a failed work-location read
 * keeps only the holidays that cover every establishment.
 */
async function holidayDaysIn(
  startKey: string,
  endKey: string,
  signal?: AbortSignal,
): Promise<Map<string, string>> {
  const [holidays, workLocations] = await Promise.all([
    unwrapList(() =>
      erpatClient.holidays.list({ start_date: startKey, end_date: endKey, per_page: 100 }, signal),
    )
      .then((res) => res.items)
      .catch(() => [] as Json[]),
    unwrap(() => erpatClient.request('/v1/api/work-locations/mine', { signal })).catch(() => null),
  ]);
  return mapHolidayDays(holidays, workLocations, startKey, endKey);
}

/**
 * The caller's own leave over a span, read ONCE from `/me/leaves` and split two
 * ways from the same rows, so the two can never disagree:
 *   - `cover` — date → leave type title for the days approved PAID leave
 *     covers (`mapLeaveDays`, the server's `leave_cover()` rule): what turns a
 *     day `leave` instead of absent;
 *   - `apps`  — date → the pending / endorsed / approved applications reaching
 *     that date (`mapLeaveApplicationsByDay`): what an Absent day shows so the
 *     employee can see the request covering it and where it stands.
 *
 * NO status filter on the request — the second map needs the undecided
 * applications, and `mapLeaveDays` filters approved+paid itself (it always did,
 * against a backend that ignored the filter). One page is enough: the window is
 * at most a pay period, and the endpoint matches an application overlapping it
 * by either end, so 100 applications touching one span is not a real employee.
 * Never rejects, like `holidayDaysIn`: a failed read marks no day on leave and
 * shows no application (the schedule still renders, and on the cut-off the
 * summary's own verdict still covers every day it has judged).
 */
async function leavesIn(
  startKey: string,
  endKey: string,
  signal?: AbortSignal,
): Promise<{ cover: Map<string, string>; apps: Map<string, DayLeaveVM[]> }> {
  const leaves = await unwrapList(() =>
    erpatClient.leaves.list({ start_date: startKey, end_date: endKey, per_page: LEAVES_MAX_PAGE_SIZE }, signal),
  )
    .then((res) => res.items)
    .catch(() => [] as Json[]);
  return {
    cover: mapLeaveDays(leaves, startKey, endKey),
    apps: mapLeaveApplicationsByDay(leaves, startKey, endKey),
  };
}

export const scheduleService = {
  get: (signal?: AbortSignal): Promise<Json> =>
    unwrap(() => erpatClient.schedule.list(undefined, signal)) as Promise<Json>,
  today: (signal?: AbortSignal): Promise<Json> =>
    unwrap(() => erpatClient.schedule.today(signal)) as Promise<Json>,
  list: (query?: ListQuery, signal?: AbortSignal): Promise<Paged<Json>> =>
    unwrapList(() => erpatClient.schedule.list(query, signal)),
  /**
   * Home "This Week Overview": the caller's seven-day shift schedule for one
   * week (Sunday-first) overlaid with their own attendance, so each day reads
   * Present / Absent / Scheduled / Today / Rest — or Holiday / On Leave. The
   * weekly schedule (`/me/schedule`), that week's attendance (`/me/attendance`
   * with a date window), the non-working holidays and the caller's leave over
   * the week (approved paid leave marks a day On Leave; every pending, endorsed
   * or approved application rides along on its days) are fetched in parallel;
   * attendance, holidays and leave each degrade to empty on failure so the
   * schedule still renders.
   *
   * `weekOffset` picks the week relative to the current one (0 = this week,
   * -1 = last week, …). Any offset is accepted here; the Home widget is what
   * stops the arrows at the current week. Statuses are still judged against the
   * real today, so an earlier week reads present / pending / absent / rest.
   */
  week: async (signal?: AbortSignal, weekOffset = 0): Promise<WeekScheduleVM> => {
    // Step by calendar days (not 7×24h) so a DST change can't shift the week.
    const ref = new Date();
    ref.setDate(ref.getDate() + weekOffset * 7);
    const weekDates = currentWeekDates(ref);
    const start = localDateKey(weekDates[0]);
    const end = localDateKey(weekDates[weekDates.length - 1]);
    const [sched, attendance, holidays, leaves] = await Promise.all([
      unwrap(() => erpatClient.schedule.list(undefined, signal)),
      unwrapList(() =>
        erpatClient.attendance.list({ start_date: start, end_date: end, per_page: 100 }, signal),
      )
        .then((res) => res.items)
        .catch(() => [] as Json[]),
      holidayDaysIn(start, end, signal),
      leavesIn(start, end, signal),
    ]);
    return mapWeekSchedule(sched, attendance, weekDates, holidays, leaves.cover, leaves.apps);
  },

  /**
   * Timesheet tab (and the Reports module): measure ONE payroll cut-off — its
   * day-by-day schedule overlaid with the caller's own attendance.
   *
   * WHERE EACH FIGURE COMES FROM. Absences, lateness and every elapsed day's
   * status come from ERPat's `/me/attendance/summary` for the cut-off measured
   * through today — the SAME request, over the same window, as the Home
   * "Absences & Tardiness" widget (`loadPayPeriodInsights`), so Home, the
   * Timesheet and the report show one set of numbers. The server applies what
   * the device cannot see: the Shift Planner, approved paid leave, holidays and
   * the grace period. Punches still come from `/me/attendance` for what the
   * summary does not carry — the day sheet's times, breaks and notes, and the
   * UNDERTIME estimate. A backend without the summary route falls back to the
   * device-side rules for the whole cut-off (see `mapCutoffPeriod`). The
   * caller's leave over the period (`/me/leaves`, every status) does two jobs
   * from one read: approved PAID leave marks the leave days the summary cannot
   * see — those still ahead in a cut-off in progress — and names the leave on
   * the day row (a judged day keeps the summary's verdict); and every pending,
   * endorsed or approved application is attached to the days it reaches, so an
   * Absent day can show the request covering it and where it stands. The
   * summary's own `holiday_pay` verdict rides on each judged day: a regular
   * holiday forfeited under the DOLE rule arrives as an absence that still
   * names its holiday.
   *
   * No read is swallowed: a failed attendance list used to become an empty one,
   * which read every elapsed workday as absent. (The holiday and leave reads
   * are the exception — a failed one marks no day, it never invents an absence.)
   *
   * The BOUNDARIES are not resolved here. `cutoff.store` owns the ladder (its
   * calendar is cached, tenant-scoped and cleared on sign-out) and the page
   * steps along it; this only measures the period it is handed, so there is no
   * second place that could disagree with payroll about where one starts.
   */
  cutoffPeriod: async (
    period: { key: string; startKey: string; endKey: string },
    signal?: AbortSignal,
  ): Promise<CutoffPeriodVM> => {
    const todayKey = localDateKey(new Date());
    // Measured through today, exactly as the Home widget asks for it: the server
    // clamps a future end anyway, and rejects a window that starts after today.
    const summaryEnd = period.endKey < todayKey ? period.endKey : todayKey;
    const [sched, attendance, holidays, summary, leaves] = await Promise.all([
      unwrap(() => erpatClient.schedule.list(undefined, signal)),
      // ONE DAY EITHER SIDE, exactly as `/me/attendance/summary` reads its own
      // window: the list matches rows by punch-IN, but a row is FILED under
      // ERPat's `log_date` (`rowDayKey`), so an overnight shift punched in on
      // the evening before the cut-off and filed under its first day would
      // otherwise be lost. `mapCutoffPeriod` keeps only the rows filed inside
      // the period; the displayed range is untouched.
      attendanceRowsIn(addCivilDays(period.startKey, -1), addCivilDays(period.endKey, 1), signal),
      holidayDaysIn(period.startKey, period.endKey, signal),
      period.startKey <= todayKey
        ? attendanceService.summaryIfAvailable({ start_date: period.startKey, end_date: summaryEnd }, signal)
        : Promise.resolve(null),
      leavesIn(period.startKey, period.endKey, signal),
    ]);

    return mapCutoffPeriod(sched, attendance, period, holidays, summary, leaves.cover, leaves.apps);
  },
};

// ── Leaves ───────────────────────────────────────────────────────────────────
export interface ApplyLeavePayload {
  /**
   * Preferred selector — the numeric leave-type id (`leave_type_id`). The API
   * types this as an INTEGER; {@link numericLeaveTypeId} coerces the mapped
   * string so a quoted id can't reach the endpoint.
   */
  leave_type_id?: string | number;
  /** Fallback selector — the leave-type label (e.g. "Regular Leave"); fuzzy-matched server-side. */
  leave_type?: string;
  start_date: string;
  end_date: string;
  reason: string;
  /** As on ERPat's web leave form (HumanResource ≥ 1.85.0 reads it; older servers ignore it). */
  duration?: LeaveDuration;
  /** Whole hours — only with `duration: 'hours'`, one of the filing-rules `hourly.options`. */
  hours?: number;
  /**
   * Supporting-document tokens staged with {@link leavesService.uploadAttachment}
   * (HumanResource ≥ 1.95.0; at most the filing-rules `attachments.max_files`).
   * Sent only when there is at least one.
   */
  attachments?: string[];
  [field: string]: unknown;
}

/** The three ways ERPat files a leave: one date, a range, or hours on one date. */
export type LeaveDuration = 'single_day' | 'multiple_days' | 'hours';

/**
 * What the Request Leave sheet emits — the hosts (Leaves page, Requests page, the
 * Home card) turn it into a POST body with {@link leaveApplyBody}.
 */
export interface LeaveRequestForm {
  /** The leave-type LABEL (the label selector fallback). */
  leaveType: string;
  /** Numeric leave-type id when known — the API's preferred selector. */
  leaveTypeId?: string;
  duration: LeaveDuration;
  startDate: string;
  /** Equal to `startDate` unless `duration` is `multiple_days`. */
  endDate: string;
  reason: string;
  /** The chosen hour option for `duration: 'hours'`, else null. */
  hours: number | null;
  /**
   * The staged supporting-document tokens to file with the leave — only when the
   * filing rules offer documents and at least one is ready. A resend after a
   * lost answer carries the SAME tokens (ERPat then answers a replay).
   */
  attachments?: string[];
}

/**
 * The `POST /v1/api/me/leaves` body for a request — ONE builder for the three
 * hosts, so they cannot drift. A single day and an hourly leave send one date as
 * both `start_date` and `end_date` (ERPat refuses anything else for them); `hours`
 * rides only with `duration: 'hours'` (ERPat refuses it otherwise, rather than
 * silently filing whole days); `attachments` rides only when there is a token,
 * through the same {@link withAttachments} rule every other write uses.
 */
export function leaveApplyBody(
  selector: Pick<ApplyLeavePayload, 'leave_type_id' | 'leave_type'>,
  form: Pick<LeaveRequestForm, 'duration' | 'startDate' | 'endDate' | 'reason' | 'hours' | 'attachments'>,
): ApplyLeavePayload {
  const body: ApplyLeavePayload = {
    ...selector,
    duration: form.duration,
    start_date: form.startDate,
    end_date: form.duration === 'multiple_days' ? form.endDate : form.startDate,
    reason: form.reason,
  };
  if (form.duration === 'hours' && form.hours !== null) body.hours = form.hours;
  // `withAttachments` returns the body untouched, or a copy plus the tokens.
  return withAttachments(body, form.attachments) as ApplyLeavePayload;
}

/**
 * Coerce a mapped leave-type id to the integer the apply endpoint documents.
 * Returns undefined for a blank or non-numeric id, so the caller falls back to
 * the `leave_type` label selector rather than posting something unusable.
 */
export function numericLeaveTypeId(id: string | number | undefined): number | undefined {
  if (id === undefined || id === null || id === '') return undefined;
  const n = Number(id);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

/**
 * Build the leave-type selector for {@link leavesService.apply}.
 *
 * The endpoint documents TWO selectors: `leave_type_id` (integer, preferred) and
 * `leave_type` (a slug/title such as vacation|sick|emergency, fuzzy-matched).
 * We now send exactly ONE:
 *
 *  - Sending both leaves it to the server which path to take, and the fuzzy
 *    matcher is the fragile one — a display label like "Service Incentive Leave
 *    (SIL)" is not the slug that matcher expects.
 *  - When no id is known we prefer the balance row's own `type`/`code` slug over
 *    the human label, which is what the matcher is documented to accept.
 *
 * `mapLeaveBalances` falls back to the row INDEX when a balance carries no
 * slug, so a purely numeric slug is rejected here — it is not a real selector
 * and would be matched against as if it were one.
 */
export function leaveTypeSelector(opts: {
  id?: string | number;
  slug?: string;
  label: string;
}): Pick<ApplyLeavePayload, 'leave_type_id' | 'leave_type'> {
  const id = numericLeaveTypeId(opts.id);
  if (id) return { leave_type_id: id };

  const slug = (opts.slug ?? '').trim();
  return { leave_type: slug && !/^\d+$/.test(slug) ? slug : opts.label };
}

/** The `/me/leaves` page-size ceiling (the endpoint defaults to 25, caps at 100). */
const LEAVES_MAX_PAGE_SIZE = 100;
/** Safety stop for the paging walk — 20 full pages is far past any real history. */
const LEAVES_MAX_PAGES = 20;

export const leavesService = {
  balances: (signal?: AbortSignal): Promise<LeaveBalanceVM[]> =>
    unwrap(() => erpatClient.leaves.balances(undefined, signal)).then(mapLeaveBalances),
  /**
   * The per-type filing windows ERPat resolved for THIS employee
   * (`GET /v1/api/me/leaves/filing-rules`, HumanResource ≥ 1.62.0), with the
   * rest days over the lookback window.
   *
   * Called through `erpatClient.request()` rather than a namespace: @erpat/sdk
   * 0.3.1 has no `leaves.filingRules` yet, and `request` is public precisely so a
   * client can reach an endpoint the SDK has not caught up with.
   *
   * ADVISORY: it lets the form refuse a date early and pin its picker
   * (`services/api/leave-filing.ts`). `POST /me/leaves` re-checks the same
   * bounds through the same trait, and the server's `today` — not the device
   * clock — is what every window is measured from.
   */
  filingRules: (signal?: AbortSignal): Promise<LeaveFilingRulesVM> =>
    unwrap(() => erpatClient.request('/v1/api/me/leaves/filing-rules', { signal })).then(mapLeaveFilingRules),
  /** ONE page of leave requests — the server's default of 25 unless `per_page` says otherwise. */
  list: (query?: ListQuery, signal?: AbortSignal): Promise<LeaveRequestVM[]> =>
    unwrap(() => erpatClient.leaves.list(query, signal)).then(mapLeaveRequests),
  /**
   * EVERY leave request, walked page by page.
   *
   * `list` hands back a single page and drops the pagination meta, so anything
   * derived from it — counts, calendar marks, status filters — silently
   * under-reports once an employee has more history than one page, with no
   * visible clue. Callers that present aggregates must use this instead.
   *
   * The rows are collected raw and mapped ONCE at the end: `mapLeaveRequests`
   * falls back to the row INDEX for an id-less row, so mapping page by page
   * would restart that counter and collide ids across pages.
   */
  listAll: async (query?: ListQuery, signal?: AbortSignal): Promise<LeaveRequestVM[]> => {
    const rows: Json[] = [];

    for (let page = 1; page <= LEAVES_MAX_PAGES; page += 1) {
      const { items, pagination } = await unwrapList<Json>(() =>
        erpatClient.leaves.list({ ...query, page, per_page: LEAVES_MAX_PAGE_SIZE }, signal),
      );
      if (!items.length) break;
      rows.push(...items);

      // The server echoes the page it served. If that isn't the page we asked
      // for — it ignored the param, or sent no meta at all — asking again would
      // just re-fetch the same rows, so stop with what we have.
      if (pagination.page !== page) break;
      if (pagination.total > 0 && rows.length >= pagination.total) break;
      // A short page is the last page. Compare against the size the server
      // actually served, which may be below the one we requested.
      const served = pagination.perPage > 0 ? pagination.perPage : LEAVES_MAX_PAGE_SIZE;
      if (items.length < served) break;
    }

    return mapLeaveRequests(rows);
  },
  teamToday: (signal?: AbortSignal): Promise<Json[]> =>
    unwrap(() => erpatClient.leaves.teamToday(undefined, signal)).then((d) => toList<Json>(d)),
  get: (id: string, signal?: AbortSignal): Promise<Json> =>
    unwrap(() => erpatClient.leaves.get(id, signal)) as Promise<Json>,
  /**
   * Apply for leave. `leaveType` MUST be the type label, e.g. "Regular Leave".
   *
   * A failure is re-thrown untouched — deliberately NOT retried. The endpoint is
   * not idempotent and a 5xx can be raised *after* the row is written, so a
   * blind retry risks filing the same leave twice. The dev log records the body
   * that was posted so a server-side fault can be matched to its input — with
   * its supporting-document tokens as a count and without the refused file's
   * name (`details.file`).
   */
  apply: async (body: ApplyLeavePayload): Promise<Json> => {
    try {
      return (await unwrap(() => erpatClient.leaves.apply(body))) as Json;
    } catch (err) {
      if (IS_DEV) {
        const e = err as { code?: string; status?: number; requestId?: string; details?: unknown };
        // No staged token and no file name reaches the console: the tokens are
        // counted, `details.file` is left out, and an API error is logged by its
        // message — the error object itself carries `details` too.
        const { attachments, ...posted } = body;
        const details =
          e.details !== null && typeof e.details === 'object' && !Array.isArray(e.details)
            ? Object.fromEntries(Object.entries(e.details).filter(([key]) => key !== 'file'))
            : e.details;
        // eslint-disable-next-line no-console
        console.error('[leaves.apply] POST /v1/api/me/leaves failed', {
          body: Array.isArray(attachments) ? { ...posted, attachments: attachments.length } : posted,
          status: e.status,
          code: e.code,
          requestId: e.requestId,
          details,
          error: err instanceof ErpatApiError ? err.message : err,
        });
      }
      throw err;
    }
  },
  cancel: (id: string): Promise<Json> => unwrap(() => erpatClient.leaves.cancel(id)) as Promise<Json>,

  // ── Supporting documents (HumanResource ≥ 1.95.0) ──────────────────────────
  // Offered only when `filingRules().attachments` is present. A document never
  // arrives with a URL: it opens through a link asked for on every open.

  /**
   * Stage ONE supporting document for a request that is not filed yet
   * (`POST /v1/api/me/leaves/uploads`, multipart `file`; needs `leave_create`).
   * Its token rides in the {@link leavesService.apply} body's `attachments`; a
   * token the sheet ends up not sending goes to {@link leavesService.discardUpload}.
   * ERPat screens the file (type, size, content) before staging it, so the
   * policy's `accept[]` is only a hint for the picker.
   *
   * Refreshes the access token and tries ONCE more on a token 401 (an access
   * token that expired while the sheet was open), as every JSON call does —
   * `withCredentialRetry` is the variant for a raw call, and refreshes only on
   * a 401 about the TOKEN. The multipart helper rebuilds the form and reads the
   * new bearer on the second try.
   */
  uploadAttachment: (file: File): Promise<MessageUploadResult> =>
    withCredentialRetry(() => uploadMessageAttachment('/v1/api/me/leaves/uploads', file)),
  /**
   * Throw away ONE staged document the sheet will not send
   * (`POST /v1/api/me/leaves/uploads/discard`). The token travels in the JSON
   * BODY, never the URL. ERPat answers 200 whatever the token's state and may
   * still refuse at the edges (422/503 — the file then expires on its own), so
   * the caller fires and forgets and never shows the outcome.
   */
  discardUpload: (token: string): Promise<void> =>
    unwrap(() =>
      erpatClient.request('/v1/api/me/leaves/uploads/discard', { method: 'POST', body: { token } }),
    ).then(() => undefined),
  /**
   * One of the caller's leaves with its supporting documents and what the
   * caller may do to them (`GET /v1/api/me/leaves/{id}` →
   * `{ leave, attachments, attachment_capabilities }`). `get` above stays the raw
   * read the notification details use.
   */
  detail: (id: string, signal?: AbortSignal): Promise<LeaveDetailVM> =>
    unwrap(() => erpatClient.leaves.get(id, signal)).then(mapLeaveDetail),
  /**
   * Add ONE document to the caller's own leave while it is undecided
   * (`POST /v1/api/me/leaves/{id}/attachments`, multipart `file` → `{ attachment }`).
   * One refresh-and-retry on a token 401, as {@link leavesService.uploadAttachment}.
   */
  addAttachment: async (id: string, file: File): Promise<LeaveAttachmentVM> => {
    const data = await withCredentialRetry(() =>
      postMultipartFile(`/v1/api/me/leaves/${encodeURIComponent(id)}/attachments`, file, 'Upload failed.'),
    );
    return mapLeaveAttachment(domainBody(data, ['attachment']));
  },
  /**
   * A FRESH link to one document of the caller's own leave
   * (`GET /v1/api/me/leaves/{id}/attachments/{file_id}`, `download=1` for a
   * download). With S3 the URL is signed for `expiresIn` (120) seconds; with
   * local storage there is none (`url: null`, `webOnly: true`) and the document
   * opens in ERPat on the web. Ask on every open; use the URL for that open only
   * — never keep, cache, persist or log it.
   */
  attachmentLink: (
    id: string,
    fileId: string,
    opts: { download?: boolean; signal?: AbortSignal } = {},
  ): Promise<LeaveAttachmentLinkVM> =>
    unwrap(() =>
      erpatClient.request(
        `/v1/api/me/leaves/${encodeURIComponent(id)}/attachments/${encodeURIComponent(fileId)}`,
        { query: opts.download ? { download: 1 } : undefined, signal: opts.signal },
      ),
    ).then(mapLeaveAttachmentLink),
  /**
   * Remove ONE document the caller uploaded to their own undecided leave
   * (`DELETE /v1/api/me/leaves/{id}/attachments/{file_id}`). The file is deleted.
   */
  removeAttachment: (id: string, fileId: string): Promise<void> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/leaves/${encodeURIComponent(id)}/attachments/${encodeURIComponent(fileId)}`, {
        method: 'DELETE',
      }),
    ).then(() => undefined),
};

/**
 * Manager-level leaves — OTHER employees' applications, with an approval flow.
 * The leave counterpart of {@link teamAttendanceService}, deliberately shaped
 * the same way so the two approval surfaces stay symmetric.
 *
 * Backed by `modules/HumanResource/controllers/TeamLeaves_euapi.php`:
 *   GET   /v1/api/leaves              — applications the caller may act on
 *   GET   /v1/api/leaves/{id}         — one application
 *   GET   /v1/api/leaves/{id}/details — the same, plus the applicant's credit ledger
 *   PATCH /v1/api/leaves/{id}/status  — approve / reject / cancel
 *   GET | POST | DELETE /v1/api/leaves/{id}/attachments… — the supporting documents
 *         (HumanResource ≥ 1.95.0): a fresh link, add one, remove one
 *
 * ⚠️ There is NO revert-to-pending. Unlike attendance, leave cannot go back: an
 * approved leave has already written a credit-deduction row, and posting
 * `pending` answers 422. Hence {@link TeamLeaveAction} is approve/reject only.
 *
 * ⚠️ The returned `status` is NOT an echo of the request. While the tenant's
 * `leave_dual_approval` setting is on, a `leave_update` holder's approve only
 * ENDORSES the application (`pending` -> `for_review`) and books no credit; only
 * a `leave_final_approver` writes `approved`. Render what comes back — the
 * response carries `result.effective_status` and `result.endorsed_only`, and
 * {@link mapTeamLeaveRecord} already prefers them over the row's own status.
 *
 * A caller with no approval scope gets 403 here (the endpoint refuses an empty
 * scope rather than degrading to "your own leave"), and a tenant with the module
 * off gets 404. `useAsyncData` maps both to `unavailable`, so the approvals page
 * shows its calm "not available" state and the Home widget hides itself.
 *
 * Authorization is left ENTIRELY to the server, which already resolves the
 * caller's ERPat role. The app deliberately does NOT pre-check a permit from
 * `/me/capabilities` before offering these actions: mirroring RBAC in a second
 * place only creates a copy that drifts, and a permit spelling this client
 * guessed wrong would lock out legitimate approvers. The per-row `can_approve`
 * verdict and the 403 are the authority; `meta.is_first_approver` /
 * `is_final_approver` are only for counting the actionable queue.
 */
export interface TeamLeavesQuery {
  page?: number;
  per_page?: number;
  /** Narrow to a single applicant (still bounded by the caller's row-scope). */
  user_id?: number;
  /**
   * Status filter. One of `pending | for_review | approved | rejected | canceled`,
   * or a comma-separated set of them. Note the API spells it **`canceled`**
   * (one l) and answers 422 `invalid_status` for anything outside that list —
   * the client-side variant is `cancelled`, so do not pass that through.
   */
  status?: string;
  /**
   * Inclusive local-date window (YYYY-MM-DD): only applications whose leave dates
   * OVERLAP it, so `start_date` = `end_date` = one day lists the leave covering that
   * day. Applied only when both are sent; a malformed date is a 422 `invalid_date`.
   */
  start_date?: string;
  end_date?: string;
  /** Narrow to one leave type. */
  leave_type_id?: number;
  /** Narrow to one department. */
  department_id?: number;
}

/**
 * What the approver queue reports about the caller and the tenant, alongside the
 * rows. Absent keys default to "single-stage, first approver" — the behaviour
 * before dual approval existed — so an older deployment degrades quietly.
 */
export interface TeamLeavesMeta {
  /** The tenant runs the two-stage workflow. */
  dualApprovalEnabled: boolean;
  /** The caller holds `leave_update` — they can endorse (or approve, when off). */
  isFirstApprover: boolean;
  /** The caller holds `leave_final_approver` — they can complete an endorsement. */
  isFinalApprover: boolean;
}

export interface TeamLeavesPage extends Paged<TeamLeaveVM> {
  meta: TeamLeavesMeta;
}

/**
 * The statuses THIS caller can actually act on, given what the queue told us.
 *
 * A first approver's backlog is `pending`. A final approver's also includes
 * `for_review`, which is precisely the stage only they can clear. With dual
 * approval off there is one stage, so `pending` is the whole queue.
 *
 * Used for badge counts. It is NOT an authorization test — the server decides
 * that per row via `can_approve`.
 */
export function actionableLeaveStatuses(meta: TeamLeavesMeta): string {
  return meta.dualApprovalEnabled && meta.isFinalApprover ? 'pending,for_review' : 'pending';
}

export const teamLeavesService = {
  /**
   * One page of the approval queue, PLUS what the server says about the caller.
   *
   * Shaped here rather than through `unwrapList`, which returns only
   * `{ items, pagination }` — the queue's `meta` also carries
   * `dual_approval_enabled` and the caller's stage, and `Pagination` has no room
   * for them. Widening that shared type for one endpoint would push three unused
   * fields onto every paged surface in the app.
   */
  list: async (query?: TeamLeavesQuery, signal?: AbortSignal): Promise<TeamLeavesPage> => {
    const data = await unwrap(() =>
      erpatClient.request('/v1/api/leaves', { query: query as Record<string, QueryValue>, signal }),
    );
    const rows = toList<Json>(data);
    const meta = asRecord(pick<unknown>(asRecord(data), ['meta'], {}));

    return {
      items: rows.map((row, i) => mapTeamLeaveRecord(row, i)),
      pagination: toPagination(meta, rows.length),
      meta: {
        dualApprovalEnabled: meta.dual_approval_enabled === true,
        // Default TRUE for the first approver: before these keys existed every
        // caller who could reach this endpoint was treated as able to decide, and
        // an older deployment must not lose its badge count.
        isFirstApprover: meta.is_first_approver !== false,
        isFinalApprover: meta.is_final_approver === true,
      },
    };
  },

  /**
   * The departments this approver can narrow the queue to (`department_id`): only
   * those holding at least one employee whose leave they may review, the caller's
   * own marked `isMine`. Same gate as `list` — a 403/404 means "not available".
   */
  departments: (signal?: AbortSignal): Promise<LeaveReviewDepartmentVM[]> =>
    unwrap(() =>
      erpatClient.request('/v1/api/leaves/departments', { signal }),
    ).then(mapLeaveReviewDepartments),

  get: (id: string, signal?: AbortSignal): Promise<TeamLeaveVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/leaves/${encodeURIComponent(id)}`, { signal }),
    ).then(mapTeamLeaveRecord),

  /**
   * One application PLUS the applicant's leave-credit position — the numbers an
   * approver actually decides on, and the twelve-month activity strip.
   *
   * A separate sub-resource because those aggregates are expensive: the credit
   * summary scans the year's ledger and the strip is a grouped twelve-month
   * query, so the list and the PATCH response deliberately do not carry them.
   * Opening the modal is still ONE request.
   */
  details: (id: string, signal?: AbortSignal): Promise<TeamLeaveVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/leaves/${encodeURIComponent(id)}/details`, { signal }),
    ).then((d) => mapTeamLeaveRecord(d)),

  /**
   * Approve or reject a leave application. Approving is what debits the
   * applicant's leave credits server-side, so this is never optimistic — the
   * caller re-reads the freshly-shaped record this returns.
   *
   * Under dual approval an `approved` request can legitimately come back as
   * `for_review`; the returned VM carries the EFFECTIVE status and
   * `endorsedOnly`, so report those and never the status you asked for.
   *
   * `remarks` carries the approver's note (the reason shown to the applicant on
   * a rejection). It is omitted from the body when blank rather than sent empty.
   */
  updateStatus: (
    id: string,
    status: TeamLeaveAction,
    remarks = '',
  ): Promise<TeamLeaveVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/leaves/${encodeURIComponent(id)}/status`, {
        method: 'PATCH',
        body: remarks.trim() ? { status, remarks: remarks.trim() } : { status },
      }),
    ).then(mapTeamLeaveRecord),

  // ── Supporting documents (HumanResource ≥ 1.95.0) ──────────────────────────
  // The same shapes as `leavesService`, on another employee's application. The
  // application itself says whether the caller may see the documents (its
  // `attachments` key is absent otherwise) and what they may change
  // (`attachment_capabilities`); these routes re-check it.

  /**
   * A FRESH link to one document of an application in the caller's scope
   * (`GET /v1/api/leaves/{id}/attachments/{file_id}`, `download=1` for a
   * download). As {@link leavesService.attachmentLink}: ask on every open, a
   * `null` URL means "open it in ERPat on the web", and the URL is never kept.
   */
  attachmentLink: (
    id: string,
    fileId: string,
    opts: { download?: boolean; signal?: AbortSignal } = {},
  ): Promise<LeaveAttachmentLinkVM> =>
    unwrap(() =>
      erpatClient.request(
        `/v1/api/leaves/${encodeURIComponent(id)}/attachments/${encodeURIComponent(fileId)}`,
        { query: opts.download ? { download: 1 } : undefined, signal: opts.signal },
      ),
    ).then(mapLeaveAttachmentLink),
  /**
   * Add ONE document to an undecided application
   * (`POST /v1/api/leaves/{id}/attachments`, multipart `file` → `{ attachment }`;
   * a `leave_update` holder over the employee).
   */
  addAttachment: async (id: string, file: File): Promise<LeaveAttachmentVM> => {
    // One refresh-and-retry on a token 401, as `leavesService.uploadAttachment`.
    const data = await withCredentialRetry(() =>
      postMultipartFile(`/v1/api/leaves/${encodeURIComponent(id)}/attachments`, file, 'Upload failed.'),
    );
    return mapLeaveAttachment(domainBody(data, ['attachment']));
  },
  /**
   * Remove ONE document from an undecided application
   * (`DELETE /v1/api/leaves/{id}/attachments/{file_id}`; the same gate). The file is deleted.
   */
  removeAttachment: (id: string, fileId: string): Promise<void> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/leaves/${encodeURIComponent(id)}/attachments/${encodeURIComponent(fileId)}`, {
        method: 'DELETE',
      }),
    ).then(() => undefined),
};

export const leaveCreditsService = {
  list: (query?: ListQuery, signal?: AbortSignal): Promise<Paged<Json>> =>
    unwrapList(() => erpatClient.leaveCredits.list(query, signal)),
  balance: (year: number | string, signal?: AbortSignal): Promise<Json> =>
    unwrap(() => erpatClient.leaveCredits.balance(year, signal)) as Promise<Json>,
};

// ── Confidential credentials (Security PIN + password re-confirmation) ────────
/** Which secret re-confirms identity on a confidential prompt. */
export type ConfidentialFactor = 'pin' | 'password';

/**
 * Exactly one secret, tagged with its factor. Passed straight through to the
 * request body and dropped — never stored in a ref, a store or `localStorage`.
 */
export type ConfidentialCredential =
  | { factor: 'pin'; pin: string }
  | { factor: 'password'; password: string };

/**
 * What a confidential check answered. Everything the server can say about the
 * CREDENTIAL is a value here; only "couldn't check" (offline, 5xx, a 404 from a
 * backend without the endpoint, a locked ACCOUNT) is thrown, so a caller can
 * never tell the user their PIN was wrong when the check simply didn't run.
 */
export type ConfidentialOutcome =
  | { kind: 'ok'; status: SecurityPinStatusVM | null }
  | { kind: 'wrong_password'; attemptsRemaining: number | null }
  | { kind: 'wrong_pin'; attemptsRemaining: number | null }
  | { kind: 'pin_locked' }
  | { kind: 'pin_not_set' }
  | { kind: 'pin_unavailable' }
  | { kind: 'cooldown'; retryAfterSec: number | null };

/** The request body for a credential: `{pin}` XOR `{password}`, the PIN as a string. */
function credentialBody(credential: ConfidentialCredential): Json {
  return credential.factor === 'pin' ? { pin: String(credential.pin) } : { password: credential.password };
}

/** A non-negative number from a number or a numeric string; null otherwise. */
function nonNegativeNumber(value: unknown): number | null {
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && /^\s*\d+(\.\d+)?\s*$/.test(value)
        ? Number(value)
        : Number.NaN;
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Translate a thrown confidential-check error into a {@link ConfidentialOutcome}
 * — or `null` when it is not an answer about the credential, in which case the
 * caller rethrows.
 *
 * Classified by `err.code`, never by status alone: a 401 is also what an
 * expired token looks like, and a 423 is also `account_locked` (the ACCOUNT,
 * not the PIN — that one must reach the caller as an error). The status must
 * agree with the code as the server's contract pairs them
 * (`ConfidentialCredentialTrait`); a mismatch is treated as unknown.
 *
 *   401 invalid_credentials       → wrong_password
 *   401 invalid_security_pin      → wrong_pin (0 attempts left → pin_locked)
 *   423 security_pin_locked       → pin_locked
 *   409 security_pin_not_set      → pin_not_set
 *   503 security_pin_unavailable  → pin_unavailable
 *   429 rate_limited              → cooldown (details.retry_after ?? Retry-After)
 */
export function classifyCredentialError(err: unknown): ConfidentialOutcome | null {
  if (!(err instanceof ErpatApiError)) return null;
  const details = asRecord(err.details);

  switch (err.code) {
    case 'invalid_credentials':
      if (err.status !== 401) return null;
      return { kind: 'wrong_password', attemptsRemaining: nonNegativeNumber(details.attempts_remaining) };

    case 'invalid_security_pin': {
      if (err.status !== 401) return null;
      const attemptsRemaining = nonNegativeNumber(details.attempts_remaining);
      // The wrong PIN that used the LAST attempt is the one that locked it.
      return attemptsRemaining === 0 ? { kind: 'pin_locked' } : { kind: 'wrong_pin', attemptsRemaining };
    }

    case 'security_pin_locked':
      return err.status === 423 ? { kind: 'pin_locked' } : null;

    case 'security_pin_not_set':
      return err.status === 409 ? { kind: 'pin_not_set' } : null;

    case 'security_pin_unavailable':
      return err.status === 503 ? { kind: 'pin_unavailable' } : null;

    case 'rate_limited':
      if (err.status !== 429) return null;
      return {
        kind: 'cooldown',
        retryAfterSec: nonNegativeNumber(details.retry_after) ?? nonNegativeNumber(err.rateLimit?.retryAfter),
      };

    default:
      return null;
  }
}

// ── Profile + preferences (Profile tab) ──────────────────────────────────────
/**
 * A government ID as the reveal endpoint returns it: full, unmasked, keyed by
 * the same server field name `GET /me/profile` masks (`sss`, `tin`, `pag_ibig`,
 * `phil_health`, `bank_number`, and `gsis` where the column exists).
 */
export type GovernmentIdMap = Record<string, string>;

export const profileService = {
  get: (signal?: AbortSignal): Promise<ProfileVM> =>
    unwrap(() => erpatClient.profile.get(signal)).then(mapProfile),

  /**
   * ANOTHER member's profile, in the same envelope as `/me/profile` so one
   * mapper covers both.
   *
   * Deliberately thinner than the self profile: the server withholds `job_info`
   * (bank, government IDs, hire date) and blanks dob/home address, so the VM
   * comes back with those rows empty. What it does carry is identity, presence,
   * `about_me` and the top-level `employment_stage`.
   *
   * Access is scoped to the caller's messaging contacts, so a target outside
   * that scope 404s — existence is hidden, never 403. Callers should treat that
   * as "no extra detail" and fall back to whatever identity they already have,
   * NOT as an error worth showing.
   */
  getUser: (userId: string, signal?: AbortSignal): Promise<ProfileVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/users/${encodeURIComponent(userId)}/profile`, { signal }),
    ).then(mapProfile),

  update: (body: Json): Promise<Json> => unwrap(() => erpatClient.profile.update(body)) as Promise<Json>,

  /** Save the self-authored bio (`users_meta.about_me`). Server caps it at 1000 chars. */
  updateAbout: (aboutMe: string): Promise<ProfileVM> =>
    unwrap(() => erpatClient.profile.update({ about_me: aboutMe })).then(mapProfile),

  /**
   * Re-confirm identity — the Security PIN OR the account password — and get the
   * caller's OWN government IDs UNMASKED.
   *
   * `GET /me/profile` masks these to the last 4 SERVER-SIDE, so the full numbers
   * are simply not in the app until this call — an eye that only un-hid what was
   * already downloaded could never show more than `****1234`. Verification and
   * disclosure are the same round trip, so the credential authorizes the DATA
   * rather than just the screen. (The only confidential value where that is
   * true: pay, salary and earnings figures are protected on screen only.)
   *
   * Exactly one of `{pin}` / `{password}` is sent; the PIN travels as a STRING
   * so a leading zero survives.
   *
   * Through {@link withCredentialRetry}: a wrong PIN or password is a 401 that
   * must NOT refresh the token, or every typo would rotate the refresh token and
   * a failed refresh would sign the user out. Only a token-shaped 401 refreshes.
   *
   * Throws `ErpatApiError`. Classify it with {@link classifyCredentialError}
   * (wrong PIN / password, locked, cooldown, …); a 404 means a backend that
   * predates the endpoint, which the caller must not phrase as a wrong
   * credential — see `ProfilePage.vue`.
   */
  revealGovernmentIds: async (credential: ConfidentialCredential): Promise<GovernmentIdMap> => {
    const res = await withCredentialRetry(() =>
      erpatClient.request<Json>('/v1/api/me/profile/government-ids', {
        method: 'POST',
        body: credentialBody(credential),
      }),
    );
    const data: Json = asRecord(res.data);

    const ids = asRecord(pick<unknown>(asRecord(data), ['government_ids'], {}));
    const out: GovernmentIdMap = {};
    for (const [key, value] of Object.entries(ids)) {
      if (value != null && value !== '') out[key] = String(value).trim();
    }
    return out;
  },
};

export interface PreferencesPayload {
  notifications?: boolean;
  /** App-level field; mapped to the server's `theme_mode`. */
  theme?: 'system' | 'light' | 'dark';
  language?: string;
}
export const preferencesService = {
  get: (signal?: AbortSignal): Promise<PreferencesVM> =>
    unwrap(() => erpatClient.preferences.get(signal)).then(mapPreferences),
  update: (body: PreferencesPayload): Promise<PreferencesVM> => {
    // Server expects `theme_mode` (not `theme`); only send provided keys.
    const server: Record<string, unknown> = {};
    if (body.notifications !== undefined) server.notifications = body.notifications;
    if (body.theme !== undefined) server.theme_mode = body.theme;
    if (body.language !== undefined) server.language = body.language;
    return unwrap(() => erpatClient.preferences.update(server)).then(mapPreferences);
  },
};

export const capabilitiesService = {
  get: (signal?: AbortSignal): Promise<Json> =>
    unwrap(() => erpatClient.capabilities.get(signal)) as Promise<Json>,
  /** Mapped view of the caller's capabilities — module flags + permit gates. */
  summary: (signal?: AbortSignal): Promise<CapabilitiesVM> =>
    unwrap(() => erpatClient.capabilities.get(signal)).then(mapCapabilities),
};

export const passwordService = {
  /**
   * Change the account password.
   *
   * Through {@link withCredentialRetry}, not `unwrap()`: a wrong CURRENT password
   * is a 401 `invalid_credentials`, and the blanket refresh-and-retry would rotate
   * the refresh token on every typo — and sign the user out if that refresh then
   * failed. Only a token-shaped 401 refreshes here.
   */
  change: async (currentPassword: string, newPassword: string): Promise<Json> => {
    const res = await withCredentialRetry(() =>
      erpatClient.password.change({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: newPassword,
      }),
    );
    return res.data as Json;
  },

  /**
   * @deprecated LEGACY FALLBACK ONLY — use {@link confidentialService.verify}.
   *
   * Used solely when the backend 404s `POST /v1/api/me/confidential/verify`
   * (an ERPat that predates the Security PIN), and for the PASSWORD factor only:
   * never send a PIN here.
   *
   * Re-confirms the signed-in user's password by re-POSTing the LOGIN endpoint
   * and throwing the answer away. Resolves `true` when the password is correct,
   * `false` when it is not, and throws for anything else (offline, 5xx, rate
   * limit) so callers can tell "wrong password" apart from "couldn't check".
   *
   * Why it is a fallback and nothing more: every check it makes COUNTS AGAINST
   * `users.login_attempts` — three typos at a reveal prompt disable the
   * account — resets any real sign-in brute-force counter on success, mints a
   * hidden session per check, and can email a one-time sign-in code.
   *
   * Three deliberate choices, kept while it exists:
   *
   * 1. `erpatClient.request()`, NOT `erpatClient.auth.login()` — the namespace
   *    method calls `cacheTokensFromResponse()`, which would overwrite the live
   *    session's tokens with this throwaway pair. Do not "simplify" this back.
   * 2. `authenticated: false` — the login endpoint is public. `X-Company-Key`
   *    still rides along from the client, which is what resolves the tenant.
   * 3. No `deviceLoginFields()` — device metadata is for real sign-ins, so these
   *    checks stay out of the user's device list.
   */
  verify: async (email: string, password: string): Promise<boolean> => {
    if (!email || !password) return false;
    try {
      await erpatClient.request('/v1/api/auth/login', {
        method: 'POST',
        body: { email, password },
        authenticated: false,
      });
      return true;
    } catch (err) {
      if (err instanceof ErpatApiError) {
        // An MFA challenge is a PASS: the server only issues one after the
        // credentials check out, so the password was right. (It can arrive as
        // a 2xx body — handled by the `return true` above — or as an error
        // envelope, depending on how the tenant is configured.)
        if (err.code === 'mfa_required') return true;
        // 401/403 = wrong password. 422 = the payload was rejected outright
        // (e.g. a blank field slipped through) — also "not verified", never a
        // reason to unmask.
        if (err.status === 401 || err.status === 403 || err.status === 422) return false;
      }
      // Network failure, 5xx, 429 — we genuinely don't know. Let it throw so
      // the caller shows "couldn't verify" instead of accusing the user of
      // typing the wrong password.
      throw err;
    }
  },
};

// ── Security PIN (a 4-digit alternative to the password on confidential prompts) ──
/**
 * The caller's own Security PIN: `GET|POST /v1/api/me/security-pin`,
 * `POST /v1/api/me/security-pin/remove`.
 *
 * What the PIN protects, stated plainly: government IDs are released by the
 * server only against the PIN or password. Pay, salary and earnings figures are
 * protected ON SCREEN only — their GET endpoints still answer to the bearer
 * token alone.
 *
 * Every write carries the account PASSWORD (a PIN is never set or removed on
 * the strength of a token, nor on the old PIN), so writes go through
 * {@link withCredentialRetry}: a wrong password is a credential answer, not a
 * reason to rotate the token. Nothing here stores or logs a PIN or password.
 */
export const securityPinService = {
  /** Current state. A 404 means a backend without the feature — see `security.store.ts`. */
  status: (signal?: AbortSignal): Promise<SecurityPinStatusVM> =>
    unwrap(() => erpatClient.request<Json>('/v1/api/me/security-pin', { signal })).then(mapSecurityPinStatus),

  /**
   * Set or change the PIN (`{password, pin}`). A weak or malformed PIN is a 422
   * with `details.fields.pin` ("is too easy to guess" / "must be exactly 4
   * digits"); a wrong password is a 401 `invalid_credentials` — classify either
   * with {@link classifyCredentialError} / the error's `details`.
   */
  save: async (password: string, pin: string): Promise<SecurityPinStatusVM> => {
    const res = await withCredentialRetry(() =>
      erpatClient.request<Json>('/v1/api/me/security-pin', {
        method: 'POST',
        body: { password, pin: String(pin) },
      }),
    );
    return mapSecurityPinStatus(res.data);
  },

  /** Remove the PIN (`{password}`). Every prompt goes back to the password. */
  remove: async (password: string): Promise<SecurityPinStatusVM> => {
    const res = await withCredentialRetry(() =>
      erpatClient.request<Json>('/v1/api/me/security-pin/remove', {
        method: 'POST',
        body: { password },
      }),
    );
    return mapSecurityPinStatus(res.data);
  },
};

/** True when a response body carries a `security_pin` status block to adopt. */
function hasSecurityPinBlock(data: unknown): boolean {
  const block = asRecord(data).security_pin;
  return !!block && typeof block === 'object' && !Array.isArray(block);
}

/**
 * The one re-confirmation call for confidential values:
 * `POST /v1/api/me/confidential/verify` with `{pin}` XOR `{password}`.
 *
 * Replaces the old login-replay check ({@link passwordService.verify}): a wrong
 * PIN or password here never touches `login_attempts`, mints no session and
 * sends no sign-in code. A correct PASSWORD also clears a locked PIN, so the
 * success carries the fresh status for the caller to adopt.
 */
export const confidentialService = {
  /**
   * Resolves an outcome for every answer about the credential (see
   * {@link ConfidentialOutcome}); THROWS for anything else — offline, 5xx, a
   * locked account, and a 404 from a backend without the endpoint (the caller
   * decides the fallback; never send a PIN to the legacy check).
   */
  verify: async (credential: ConfidentialCredential): Promise<ConfidentialOutcome> => {
    try {
      const res = await withCredentialRetry(() =>
        erpatClient.request<Json>('/v1/api/me/confidential/verify', {
          method: 'POST',
          body: credentialBody(credential),
        }),
      );
      return { kind: 'ok', status: hasSecurityPinBlock(res.data) ? mapSecurityPinStatus(res.data) : null };
    } catch (err) {
      const outcome = classifyCredentialError(err);
      if (outcome) return outcome;
      throw err;
    }
  },
};

// ── AI Assistant (AI tab) ────────────────────────────────────────────────────
export const assistantService = {
  conversations: (query?: ListQuery, signal?: AbortSignal): Promise<AssistantConversationVM[]> =>
    unwrap(() => erpatClient.assistant.listConversations(query, signal)).then(mapAssistantConversationList),
  conversation: (id: string, signal?: AbortSignal): Promise<AssistantConversationVM> =>
    unwrap(() => erpatClient.assistant.getConversation(id, signal)).then(mapAssistantConversation),
  start: (message: string): Promise<AssistantConversationVM> =>
    unwrap(() => erpatClient.assistant.createConversation({ body: message })).then(mapAssistantConversation),
  // Returns only the new assistant reply (`{ message }`), not the full transcript.
  send: (id: string, message: string): Promise<AssistantMessageVM> =>
    unwrap(() => erpatClient.assistant.sendMessage(id, { body: message })).then(mapAssistantReply),
  suggestions: (signal?: AbortSignal): Promise<AssistantSuggestionVM[]> =>
    unwrap(() => erpatClient.assistant.suggestions(undefined, signal)).then(mapAssistantSuggestions),
  // SSE streaming send: opens `POST /conversations` (new) or
  // `/conversations/{id}/messages` (existing) with `stream: true` so the reply
  // arrives token-by-token and the connection stays alive during a long
  // generation. `onMeta` yields the conversation id in-band (crucial for a new
  // thread); resolves with the full text on the terminal `[DONE]`. See
  // `assistant-stream.ts` for the SSE contract + error handling.
  stream: (
    target: { conversationId?: string; prompt: string },
    handlers: AssistantStreamHandlers,
    signal?: AbortSignal,
  ): Promise<AssistantStreamResult> => streamAssistant(target, handlers, signal),
};

// ── Messages (Messages tab) ──────────────────────────────────────────────────
/**
 * A staged attachment — a message, ticket, to-do comment or leave document.
 * `token` is passed back in the write that sends it.
 */
export interface MessageUploadResult {
  token: string;
  name: string;
  size: number;
}

/**
 * POST one file as multipart field `file` to a euapi path and return the
 * envelope's `data` — THE transport for every upload (leave documents, messages,
 * tickets, to-do comments, forms). `@erpat/sdk`'s `request()` is JSON-only, so
 * this posts `FormData` directly with the Authorization + tenant headers the SDK
 * would send, through `sessionFetch` so a sign-out cancels an upload in flight.
 *
 * Beyond what the SDK reads, two answers that are not ERPat's own:
 *  - a 413 is a web server refusing the body BEFORE PHP runs — its own HTML
 *    page, never an envelope — so it reads as `file_too_large`, page unread;
 *  - a body that is not JSON (an error page, the tenant `/install` redirect)
 *    rejects as `invalid_response`, exactly as the SDK's own requests do, never
 *    as a `SyntaxError` — `errorMessage()` already words that.
 * A refusal keeps its `details` (only when an object: ERPat's empty details is
 * PHP's `[]`) and the `X-Request-Id`, so a caller can branch on the code and the
 * backend log entry stays findable.
 */
async function postMultipartFile(path: string, file: File, failMessage: string): Promise<Json> {
  const form = new FormData();
  form.append('file', file);

  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = erpatClient.getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const companyKey = getStoredCompanyKey();
  if (companyKey) headers['X-Company-Key'] = companyKey;

  let res: Response;
  try {
    // sessionFetch: a sign-out cancels an upload still in flight.
    res = await sessionFetch(`${API_BASE_URL}${path}`, { method: 'POST', headers, body: form });
  } catch (err) {
    throw new ErpatApiError('network_error', err instanceof Error ? err.message : String(err), 0);
  }

  if (res.status === 413) {
    // The web server's page: nobody will read it, so release the connection.
    void res.body?.cancel().catch(() => {});
    throw new ErpatApiError('file_too_large', 'This file is larger than the limit.', 413);
  }

  const requestId = res.headers.get('X-Request-Id') ?? undefined;
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new ErpatApiError('invalid_response', 'Upload response was not valid JSON.', res.status, { requestId });
    }
  }

  const envelope = asRecord(parsed);
  if (!res.ok || envelope.ok === false) {
    const e = asRecord(envelope.error);
    const rawDetails = e.details;
    throw new ErpatApiError(
      typeof e.code === 'string' && e.code !== '' ? e.code : `http_${res.status}`,
      typeof e.message === 'string' && e.message !== '' ? e.message : failMessage,
      res.status,
      {
        details:
          rawDetails !== null && typeof rawDetails === 'object' && !Array.isArray(rawDetails)
            ? (rawDetails as Json)
            : undefined,
        requestId,
      },
    );
  }
  return asRecord(envelope.data);
}

/**
 * Upload one attachment via {@link postMultipartFile} to a euapi uploads path,
 * returning the staged token. The file is validated server-side against the
 * tenant's accepted file-format setting; the token is submitted with the write
 * that sends it (a message, a reply, a ticket, a leave).
 */
async function uploadMessageAttachment(path: string, file: File): Promise<MessageUploadResult> {
  const data = await postMultipartFile(path, file, 'Upload failed.');
  const fileData = asRecord(data.file);
  return {
    token: String(fileData.token ?? ''),
    name: String(fileData.name ?? file.name),
    size: typeof fileData.size === 'number' ? fileData.size : file.size,
  };
}

/** Only include `attachments` in a body when there are staged tokens. */
function withAttachments(body: Json, attachments?: string[]): Json {
  return attachments && attachments.length ? { ...body, attachments } : body;
}

/**
 * What an idempotent message write answers (phase6-contract.md §3.2.3).
 *
 * `replayed: true` means the server already held a message for this
 * `client_event_id` with the SAME content and returned the ORIGINAL id — the
 * send is delivered, nothing was written twice. A changed payload under the
 * same key is a 409 `idempotency_conflict` (`ErpatApiError.code`), which the
 * outbox marks terminal; branch on the code, never on the message text.
 */
export interface MessageSendResult {
  messageId: string;
  replayed: boolean;
}

function mapSendResult(data: unknown): MessageSendResult {
  const rec = asRecord(data);
  const id = pick<unknown>(rec, ['message_id', 'id'], '');
  return {
    messageId: typeof id === 'number' ? String(id) : typeof id === 'string' ? id : '',
    replayed: rec.replayed === true,
  };
}

/**
 * Attach the outbox's idempotency key. The SDK body is a free-form record, so
 * the server field rides along without an SDK change (DEC-089). The key is
 * REQUIRED and must be a UUID v4: a malformed one is refused HERE, before any
 * request, as `ErpatApiError('invalid_client_event_id', …, 0)` — it is never
 * silently stripped, because an un-keyed retry could deliver a message twice.
 */
function withClientEventId(body: Json, clientEventId: string): Json {
  if (!isUuidV4(clientEventId)) {
    throw new ErpatApiError('invalid_client_event_id', 'A message send needs a UUID v4 client_event_id.', 0);
  }
  return { ...body, client_event_id: clientEventId.toLowerCase() };
}

export const messagesService = {
  list: (query?: ListQuery, signal?: AbortSignal): Promise<Conversation[]> =>
    unwrap(() => erpatClient.messages.list(query, signal)).then((d) => mapConversationList(d, 'direct')),
  // `meId` (the signed-in user's id) lets the mapper pick the OTHER participant's
  // name out of the thread's messages — the detail response has no counterpart obj.
  thread: (id: string, query?: ListQuery, signal?: AbortSignal, meId?: string): Promise<Conversation> =>
    unwrap(() => erpatClient.messages.thread(id, query, signal)).then((d) => mapConversation(d, 0, 'direct', meId)),
  unreadCount: (signal?: AbortSignal): Promise<number> =>
    unwrap(() => erpatClient.messages.unreadCount(signal)).then((d) => readCount(d)),
  // POST /me/messages requires { to_user_id, message } (verified against the API).
  // `attachments` are staged tokens from `upload()`; message may be '' when files are attached.
  // `clientEventId` (UUID v4, REQUIRED) makes the write idempotent — see MessageSendResult.
  start: (toUserId: number | string, message: string, attachments: string[] | undefined, clientEventId: string): Promise<MessageSendResult> =>
    unwrap(() =>
      erpatClient.messages.send(withClientEventId(withAttachments({ to_user_id: toUserId, message }, attachments), clientEventId)),
    ).then(mapSendResult),
  reply: (id: string, message: string, attachments: string[] | undefined, clientEventId: string): Promise<MessageSendResult> =>
    unwrap(() =>
      erpatClient.messages.reply(id, withClientEventId(withAttachments({ message }, attachments), clientEventId)),
    ).then(mapSendResult),
  read: (id: string): Promise<Json> => unwrap(() => erpatClient.messages.read(id)) as Promise<Json>,
  /** Stage one attachment for a direct message; returns the token to send. */
  upload: (file: File): Promise<MessageUploadResult> =>
    uploadMessageAttachment('/v1/api/me/messages/uploads', file),
};

// The members endpoint isn't in the SDK yet. We probe it via the low-level
// `request()` once per session: a 404/405 marks it absent so we stop hitting a
// route the backend hasn't shipped, and callers fall back to the thread-derived
// roster (`conversation.members`) until then.
let groupMembersEndpoint: 'unknown' | 'present' | 'absent' = 'unknown';

export const messageGroupsService = {
  list: (query?: ListQuery, signal?: AbortSignal): Promise<Conversation[]> =>
    unwrap(() => erpatClient.messageGroups.list(query, signal)).then((d) => mapConversationList(d, 'group')),
  // `meId` (the signed-in user's id) isn't needed for the group header name, but
  // it lets the mapper mark the current user's own messages as "mine" (right-
  // aligned) when the server omits an explicit self flag on group message rows.
  thread: (id: string, query?: ListQuery, signal?: AbortSignal, meId?: string): Promise<Conversation> =>
    unwrap(() => erpatClient.messageGroups.thread(id, query, signal)).then((d) => mapConversation(d, 0, 'group', meId)),
  send: (id: string, message: string, attachments: string[] | undefined, clientEventId: string): Promise<MessageSendResult> =>
    unwrap(() =>
      erpatClient.messageGroups.sendMessage(id, withClientEventId(withAttachments({ message }, attachments), clientEventId)),
    ).then(mapSendResult),
  read: (id: string): Promise<Json> => unwrap(() => erpatClient.messageGroups.read(id)) as Promise<Json>,
  /** Stage one attachment for a group message; returns the token to send. */
  upload: (groupId: string, file: File): Promise<MessageUploadResult> =>
    uploadMessageAttachment(`/v1/api/me/message-groups/${encodeURIComponent(groupId)}/uploads`, file),
  /**
   * Full participant roster from the optional members endpoint, or `null` when
   * it isn't available yet — callers then fall back to the thread-derived
   * `conversation.members`. Probed once per session (see {@link groupMembersEndpoint}).
   */
  members: async (id: string, signal?: AbortSignal): Promise<GroupMember[] | null> => {
    if (groupMembersEndpoint === 'absent') return null;
    try {
      const data = await unwrap(() =>
        erpatClient.request(`/v1/api/me/message-groups/${encodeURIComponent(id)}/members`, { signal }),
      );
      groupMembersEndpoint = 'present';
      return mapGroupMembers(data);
    } catch (err) {
      // 404/405 → route doesn't exist; mark absent. Other errors (offline, 5xx,
      // an /install HTML redirect) are transient — keep probing next time.
      if (err instanceof ErpatApiError && (err.status === 404 || err.status === 405)) {
        groupMembersEndpoint = 'absent';
      }
      return null;
    }
  },
};

export const contactsService = {
  list: (query?: ListQuery, signal?: AbortSignal): Promise<Contact[]> =>
    unwrap(() => erpatClient.contacts.list(query, signal)).then(mapContacts),
};

// ── Presence ─────────────────────────────────────────────────────────────────
export const presenceService = {
  /**
   * Tell the server the user is still here. The response is incidental — reaching the endpoint
   * at all is what refreshes `users.last_online`, because every authenticated request does.
   *
   * POST, not GET, on purpose: the service worker caches GETs, and a heartbeat served from cache
   * is a heartbeat the server never saw. See services/api/presence-heartbeat.ts for the cadence.
   */
  ping: (signal?: AbortSignal): Promise<Json> =>
    unwrap(() => erpatClient.request('/v1/api/me/presence', { method: 'POST', signal })) as Promise<Json>,
};

// ── Notifications (Inbox) ────────────────────────────────────────────────────
export const notificationsService = {
  /**
   * The server's list is authoritative; the inbox projects the notifications
   * store's per-item state (revision-aware, phase6-contract.md §3.3) over
   * these rows — the old device-memory read overlay is gone (DEC-084).
   */
  list: (query?: ListQuery, signal?: AbortSignal): Promise<NotificationItem[]> =>
    unwrap(() => erpatClient.notifications.list(query, signal))
      .then(mapNotifications),
  /**
   * The same list with its envelope's live `unread_count` kept — the
   * notifications store's `refresh()` source: the canonical rows re-floor the
   * held items and the count is the badge's server term (§3.3). One request,
   * not a list plus `/unread-count`.
   */
  unreadCount: (query?: ListQuery, signal?: AbortSignal): Promise<NotificationListWithCount> =>
    unwrap(() => erpatClient.notifications.list(query, signal))
      .then(mapNotificationListWithCount),
  read: (id: string): Promise<Json> => unwrap(() => erpatClient.notifications.read(id)) as Promise<Json>,
  readAll: (): Promise<Json> => unwrap(() => erpatClient.notifications.readAll()) as Promise<Json>,
  dismiss: (id: string): Promise<Json> => unwrap(() => erpatClient.notifications.dismiss(id)) as Promise<Json>,
};

// ── Realtime (Phase 1) ───────────────────────────────────────────────────────
/**
 * Facade over `services/api/realtime.api.ts` for views. The socket itself is
 * owned by `services/realtime.service.ts` (which talks to the api module
 * directly); screens only ever need the bootstrap snapshot and the
 * "send me a test" action.
 */
export const realtimeService = {
  bootstrap: (signal?: AbortSignal): Promise<RealtimeBootstrap> => fetchRealtimeBootstrap(signal),
  /** Publish a test envelope to the caller's own personal channel. */
  sendTest: (signal?: AbortSignal): Promise<{ eventId: string }> => sendRealtimeTest(signal),
};

// ── Advisories / Announcements ───────────────────────────────────────────────
/**
 * Every advisory in the organisation that this employee is a recipient of.
 *
 * That is two conditions, and no single endpoint answers both:
 *
 * WHICH ADVISORIES EXIST — the broad routes. `/me/advisories` is documented as
 * "self-targeted advisories WITHIN THE ACTIVE WINDOW", so it structurally
 * cannot return last quarter's expired notice, and a year/month browser built
 * on it would only ever show what is live today. The broad query below asks for
 * the full record instead (all, inactive, expired). These routes are NOT in
 * `docs/openapi.json`, hence the probing and the tolerance for 403/404/405 — a
 * tenant may not expose them at all.
 *
 * WHETHER IT IS MINE — the broad routes are audience-agnostic, so membership is
 * decided here (see `isAddressedToMe`). An advisory the self route returned is
 * mine by definition; beyond that, a broad record naming an audience is checked
 * against the signed-in user, and one naming none is treated as an org-wide
 * broadcast.
 *
 * READ / PINNED STATE — only `/me/advisories` carries it. It returns `is_read`
 * / `is_pinned` per user, the broad routes return neither, and the app WRITES
 * that state through `/me/advisories/{id}/read|pin`. The unread count, unread
 * rail, Read/Unread badge and pin toggle all read those two fields, so the self
 * list is fetched alongside and OVERLAID by id. An advisory it never returned
 * (an expired one, say) has no state to overlay: `readTracked` stays false and
 * the card shows its neutral "Advisory" badge instead of a wrong Read/Unread.
 *
 * If a tenant exposes no broad route, the self list stands alone — the
 * behaviour before any of this existed.
 */
const BROAD_ADVISORY_QUERY: ListQuery = {
  page: 1,
  per_page: 100,
  include_all: true,
  include_inactive: true,
  include_expired: true,
  active_only: false,
};

const ADVISORY_LIST_ENDPOINTS = [
  '/v1/api/advisories',
  '/v1/api/announcements',
] as const;

/** `per_page` on `/me/advisories`: "Default 25, max 100." */
const ADVISORY_MAX_PAGE_SIZE = 100;
/** Safety stop for the paging walk. */
const ADVISORY_MAX_PAGES = 20;

function mergeAdvisoryQuery(query?: ListQuery): ListQuery {
  return { ...BROAD_ADVISORY_QUERY, ...(query ?? {}) };
}

function isOptionalAdvisoryEndpointError(err: unknown): boolean {
  if (!(err instanceof ErpatApiError)) return false;
  return [403, 404, 405].includes(err.status) || err.code === 'invalid_response';
}

/**
 * Every self-targeted advisory, walked page by page — the endpoint defaults to
 * 25 per page, and the page filters and counts client-side.
 */
async function selfAdvisories(query?: ListQuery, signal?: AbortSignal): Promise<AdvisoryVM[]> {
  const rows: Json[] = [];

  for (let page = 1; page <= ADVISORY_MAX_PAGES; page += 1) {
    const result = await unwrapList<Json>(() =>
      erpatClient.advisories.list({ ...query, page, per_page: ADVISORY_MAX_PAGE_SIZE }, signal),
    );
    if (!result.items.length) break;
    rows.push(...result.items);

    // The server echoes the page it served. If that isn't the page we asked for
    // — it ignored the param, or sent no meta — asking again re-fetches the
    // same rows, so stop with what we have.
    if (result.pagination.page !== page) break;
    if (result.pagination.total > 0 && rows.length >= result.pagination.total) break;
    const served = result.pagination.perPage > 0 ? result.pagination.perPage : ADVISORY_MAX_PAGE_SIZE;
    if (result.items.length < served) break;
  }

  return rows.map((item) => mapAdvisory(item));
}

/**
 * The tenant's whole notice board, from whichever broad route this token can
 * reach.
 *
 * Best-effort: the routes are undocumented, so ANY failure resolves empty and
 * the self-targeted list carries the page on its own. An abort is the
 * exception — that is the caller unmounting.
 */
async function broadAdvisories(query: ListQuery, signal?: AbortSignal): Promise<AdvisoryVM[]> {
  for (const path of ADVISORY_LIST_ENDPOINTS) {
    try {
      const result = await unwrapList(() =>
        erpatClient.request(path, { query: query as Record<string, QueryValue>, signal }),
      );
      return result.items.map((item) => mapAdvisory(item));
    } catch (err) {
      if (signal?.aborted) throw err;
      if (!isOptionalAdvisoryEndpointError(err)) break;
    }
  }

  return [];
}

/**
 * What the self list contributes to a broad record, keyed by advisory id: this
 * user's read/pinned state, plus its `files` for a broad record that sent none.
 */
type AdvisoryReadState = Pick<AdvisoryVM, 'unread' | 'pinned' | 'readTracked' | 'files'>;

/**
 * Is this broad-route advisory addressed to the signed-in user?
 *
 * Three cases, in order:
 *  - `selfIds` holds it — the server already said it is theirs. Decided.
 *  - It names no audience — an org-wide broadcast, which every employee is a
 *    recipient of. An advisory addressed to nobody would be pointless, so a
 *    missing audience is read as "everyone", not "no one".
 *  - It names an audience — membership is checked against the token's user id.
 *
 * With no readable user id there is nothing to check membership against, so
 * audience-bearing records are dropped rather than shown on a guess; the self
 * list still carries whatever is genuinely the user's.
 */
function isAddressedToMe(advisory: AdvisoryVM, selfIds: Set<string>, userId: string): boolean {
  if (selfIds.has(advisory.id)) return true;
  if (!advisory.audienceUserIds.length) return true;
  return Boolean(userId) && advisory.audienceUserIds.includes(userId);
}

async function listAdvisories(query?: ListQuery, signal?: AbortSignal): Promise<AdvisoryVM[]> {
  // Both at once — neither depends on the other, and the page waits on the pair.
  const [broad, self] = await Promise.all([
    broadAdvisories(mergeAdvisoryQuery(query), signal),
    selfAdvisories(query, signal),
  ]);

  // No broad route on this tenant: the self list is the whole answer.
  if (!broad.length) return self;

  const selfIds = new Set(self.map((advisory) => advisory.id));
  const userId = getSessionUserId();
  const readState = new Map<string, AdvisoryReadState>(
    self.map((advisory) => [
      advisory.id,
      { unread: advisory.unread, pinned: advisory.pinned, readTracked: advisory.readTracked, files: advisory.files },
    ]),
  );

  const mine = broad
    .filter((advisory) => isAddressedToMe(advisory, selfIds, userId))
    // The broad record supplies the advisory; the self list supplies what this
    // user has read and pinned. Ids the self route never returned keep the
    // broad record's own (untracked) state. Attachments come from whichever
    // copy actually listed them — the undocumented broad routes may send none.
    .map((advisory) => {
      const state = readState.get(advisory.id);
      if (!state) return advisory;
      return { ...advisory, ...state, files: advisory.files.length ? advisory.files : state.files };
    });

  // A self-targeted advisory the broad route somehow omitted must not vanish.
  const seen = new Set(broad.map((advisory) => advisory.id));
  return [...mine, ...self.filter((advisory) => !seen.has(advisory.id))];
}

async function getAdvisory(id: string, signal?: AbortSignal): Promise<AdvisoryVM> {
  try {
    // Self route first — same split as the list, applied to one record: if this
    // advisory is one of the user's own, that copy is the one carrying the
    // `is_read` / `is_pinned` the detail page's pin toggle and mark-read-on-open
    // depend on.
    return await unwrap(() => erpatClient.advisories.get(id, signal)).then(mapAdvisory);
  } catch (err) {
    if (!isOptionalAdvisoryEndpointError(err)) {
      throw err;
    }
    // Off the self list — expired, or not addressed to this user. The broad
    // route still has the record; it just has no per-user state to attach.
    return unwrap(() =>
      erpatClient.request(`/v1/api/advisories/${encodeURIComponent(id)}`, { signal }),
    ).then(mapAdvisory);
  }
}

export const advisoriesService = {
  list: (query?: ListQuery, signal?: AbortSignal): Promise<AdvisoryVM[]> =>
    listAdvisories(query, signal),
  get: (id: string, signal?: AbortSignal): Promise<AdvisoryVM> =>
    getAdvisory(id, signal),
  create: (body: Json): Promise<Json> => unwrap(() => erpatClient.advisories.create(body)) as Promise<Json>,
  unreadCount: (signal?: AbortSignal): Promise<number> =>
    unwrap(() => erpatClient.advisories.unreadCount(signal)).then((d) => readCount(d)),
  pin: (id: string, pinned = true): Promise<Json> =>
    unwrap(() => erpatClient.advisories.pin(id, { pinned })) as Promise<Json>,
  read: (id: string, read = true): Promise<Json> =>
    unwrap(() => erpatClient.advisories.read(id, { read })) as Promise<Json>,
  /**
   * A FRESH link to one attachment — `GET /v1/api/me/advisories/{id}/files/{index}`.
   *
   * On an S3 tenant every call is newly signed, so this is what the viewer's
   * re-sign hook and the Download button use instead of the URL that arrived
   * with the advisory (which expires after the tenant's TTL, and may also be a
   * service-worker cached copy). `download: true` asks for a link that SAVES the
   * file under its real name; without it the link previews inline. A local-disk
   * file resolves to its stable /storage URL either way. The SDK has no method
   * for this route, hence the raw request (same as the broad advisory routes).
   *
   * Resolves `''` when the response carries no URL; rejects on 404
   * (`advisory_not_found` / `file_not_found`) and 503 (`file_unavailable`).
   */
  fileLink: (
    id: string,
    index: number,
    opts: { download?: boolean; signal?: AbortSignal } = {},
  ): Promise<string> =>
    unwrap(() =>
      erpatClient.request(
        `/v1/api/me/advisories/${encodeURIComponent(id)}/files/${encodeURIComponent(String(index))}`,
        { query: opts.download ? { download: 1 } : undefined, signal: opts.signal },
      ),
    ).then(mapAdvisoryFileLink),
};

// ── Payslips / Payroll ───────────────────────────────────────────────────────
// PDF is intentionally omitted from the service: the API returns 501 (no PDF
// yet) — the preview renders the /lines breakdown instead (product decision).
// The list endpoint's header `totals` are always 0 (see models.ts), so real net
// pay comes only from /lines — `totals()` exposes that for per-row enrichment.
//
// RELEASED PAYSLIPS ONLY. An employee sees a payslip only once payroll releases
// it: APPROVED, inside a COMPLETED pay run. A `pending` payslip is the payroll
// officer's unfinished, confidential work. ERPat enforces this server-side
// (Payslips_model `released_to_employee`, Compensation 1.75.0); the two clamps
// below keep an older ERPat deployment — which still served pending — from ever
// reaching the screen: the list asks for `status=approved` (honoured by every
// ERPat version), and a detail that is not approved is treated as not found.
const RELEASED_PAYSLIP_STATUS = 'approved';

export const payslipsService = {
  /** Own RELEASED payslip headers (server-paginated, newest pay-date first) + pagination meta. */
  list: (query?: ListQuery, signal?: AbortSignal): Promise<Paged<PayslipListItemVM>> =>
    unwrapList(() =>
      erpatClient.payslips.list({ ...query, status: RELEASED_PAYSLIP_STATUS }, signal),
    ).then((result) => ({
      ...result,
      items: result.items.map((item, index) => mapPayslipListItem(item, index)),
    })),
  /**
   * Real money figures for one payslip, from /lines. The list page calls this per
   * visible row to fill in net pay (the list payload's header totals are always 0).
   * Bounded by the fetched page size (≤ the list's per_page, default 25).
   */
  totals: (id: string, signal?: AbortSignal): Promise<PayslipTotalsVM> =>
    unwrap(() => erpatClient.payslips.lines(id, undefined, signal)).then(mapPayslipTotals),
  /**
   * Payslip header + /lines breakdown merged into one detail VM (real totals).
   * An unreleased payslip (see above) is reported as a 404, so the preview and a
   * notification deep link show their calm "not available" state instead of it.
   */
  detail: async (id: string, signal?: AbortSignal): Promise<PayslipDetailVM> => {
    const [header, lines] = await Promise.all([
      unwrap(() => erpatClient.payslips.get(id, signal)),
      unwrap(() => erpatClient.payslips.lines(id, undefined, signal)),
    ]);
    const detail = mapPayslipDetail(header, lines);
    if (detail.status !== RELEASED_PAYSLIP_STATUS) {
      throw new ErpatApiError('payslip_not_found', 'Payslip not found.', 404);
    }
    return detail;
  },
};

// ── Salary ───────────────────────────────────────────────────────────────────
export const salaryService = {
  /**
   * GET /me/salary/current → `{ salary: {...} | null }` — the caller's latest
   * APPROVED salary record (monthly_salary / daily_rate / hourly_rate /
   * payroll_basis / currency). `salary` is null when none exists; the endpoint
   * 403/404s when the payroll module is off for the tenant.
   */
  current: (signal?: AbortSignal): Promise<Json> =>
    unwrap(() => erpatClient.salary.current(signal)) as Promise<Json>,

  /**
   * GET /me/salary → `{ salary_history: [...], meta }` — own salary history,
   * newest effective date first, for the Salary Records page.
   *
   * SELF-ONLY and APPROVED-ONLY server-side: `user_id` is forced to the token's
   * subject, and `status` is pinned to `approved` with any caller-supplied
   * `?status=` ignored, so an unapproved raise can never be probed through here.
   * Accepts `effective_from` / `effective_to` / `payroll_basis` /
   * `salary_grade_id` / `page` / `per_page` (default 25, max 100).
   *
   * 403/404s when `module_compensation` is off for the tenant — the caller should
   * render that as "not available", not as an error.
   */
  list: (query?: ListQuery, signal?: AbortSignal): Promise<Paged<SalaryRecordVM>> =>
    unwrapList(() => erpatClient.salary.list(query, signal)).then((result) => ({
      ...result,
      items: result.items.map((item, index) => mapSalaryRecord(item, index)),
    })),
};

// ── Loans ────────────────────────────────────────────────────────────────────
/**
 * The caller's OWN loans (as borrower) — a strictly READ-ONLY surface:
 *
 *   GET /v1/api/me/loans                — own loans + meta
 *   GET /v1/api/me/loans/{id}           — one loan (404 when not the borrower)
 *   GET /v1/api/me/loans/{id}/payments  — payment history
 *   GET /v1/api/me/loans/{id}/schedule  — amortization schedule + summary
 *
 * There is no apply / edit / pay endpoint, so this service exposes no writes.
 *
 * Gated on `module_compensation` (404 when off). Self-scoping is server-side:
 * `borrower_id` is forced to the token subject, and a loan id the caller does
 * not own 404s rather than 403s, so existence is never leaked. Both map to
 * `useAsyncData`'s `unavailable`, which the pages render as "not available".
 *
 * NOTE — no status filter. The endpoint accepts `?status=` / `?statuses[]=`,
 * but `ListQuery` values are `string | number | boolean | null | undefined`, so
 * an array cannot be typed through the SDK; and even forced through it
 * serialises as `statuses=a,b` (one scalar via `searchParams.append`), which is
 * not the repeated `statuses[]` the controller parses. The four segments are
 * derived client-side from `status_key` + balance anyway (see `loanLifecycleOf`),
 * and `list()` fetches the whole set — so the param is never needed.
 */
export const loansService = {
  /**
   * Every loan the caller has, newest first.
   *
   * One request at the endpoint's ceiling rather than a paging walk: an
   * employee has a handful of loans, the server loads all rows and slices in
   * PHP regardless, and `meta` carries no `total_pages` to drive a loop with.
   */
  list: (signal?: AbortSignal): Promise<LoanVM[]> =>
    unwrap(() => erpatClient.loans.list({ per_page: 100 }, signal)).then(mapLoanList),

  /**
   * One loan plus its schedule and payments, in a single awaited bundle — so
   * the detail page runs one `useAsyncData`, shows one spinner, and refreshes
   * everything from one pull-to-refresh.
   *
   * ONLY the loan leg may reject. The sub-resources are tolerated to `null`
   * because `useAsyncData` exposes a single `errorStatus`: an un-tolerated
   * `Promise.all` would let a 404 from `/schedule` masquerade as the loan's own
   * ownership 404 and tell a genuine borrower their loan does not exist. `null`
   * means "that leg failed"; a schedule with `periodsTotal === 0` means "no
   * schedule has been generated yet" — two different things the page says
   * differently. Same tolerated-leg shape as `attendanceService.loadToday`.
   */
  detail: async (id: string, signal?: AbortSignal): Promise<LoanDetailBundle> => {
    const [loan, schedule, payments] = await Promise.all([
      unwrap(() => erpatClient.loans.get(id, signal)).then(mapLoanDetail),
      unwrap(() => erpatClient.loans.schedule(id, undefined, signal))
        .then((d) => mapLoanSchedule(d))
        .catch(() => null),
      unwrap(() => erpatClient.loans.payments(id, undefined, signal))
        .then(mapLoanPayments)
        .catch(() => null),
    ]);
    return { loan, schedule, payments };
  },
};

/** What the detail page renders: the loan, plus two independently-tolerated legs. */
export interface LoanDetailBundle {
  loan: LoanDetailVM;
  schedule: LoanScheduleVM | null;
  payments: LoanPaymentsVM | null;
}

// ── Notes ────────────────────────────────────────────────────────────────────
/**
 * App-friendly note payload. The end-user API stores the body under
 * `description` and tags under `labels` (a comma-separated string), and does
 * NOT accept `pinned` on create/update — pinning is a separate endpoint. This
 * maps the app's shape onto those server fields so call sites stay clean.
 */
export interface NoteWritePayload {
  title: string;
  /** Note body → server `description`. */
  body?: string;
  /** Tags → server `labels` (joined into a comma-separated string). */
  tags?: string[];
  /** Files selected in Syntry's note composer. Sent as multipart attachments. */
  files?: File[];
  /** Pin state. On create this is applied via a follow-up pin call. */
  pinned?: boolean;
  /**
   * Server-side category, CREATE ONLY. ERPat fixes a note's type at creation and
   * `PATCH /me/notes/{id}` fails with a 422 if it is sent, so `toServerNote()`
   * deliberately drops it and only {@link notesService.create} passes it on.
   */
  type?: NoteType;
  /**
   * The re-editable drawing behind a scribble.
   *
   * PAIRED WITH `body`, always. ERPat stores a scene and the picture it renders
   * to as one drawing described twice, and enforces it: a scene sent without a
   * `description` is rejected (422), and a `description` sent without a scene
   * CLEARS the stored one — deliberately, because a scene that no longer
   * describes the stored picture would be replayed over it on the next web edit.
   * So `toServerNote()` only forwards a scene when a body goes with it, and
   * anything editing a scribble's body must re-render its scene in the same
   * request or knowingly flatten the drawing.
   *
   * `null` explicitly drops the scene (the drawing survives as a flat picture);
   * `undefined` leaves the field out of the request entirely.
   */
  scene?: SketchScene | null;
}

/** Translate the app payload into the server's `{ title, description, labels }`. */
/**
 * The `labels` CSV ERPat expects.
 *
 * ERPat's web posts REGISTRY IDS here (`labels: 166`) and renders its chips by
 * looking them up, so a title written instead is stored but never decodes —
 * the note shows no label in the web. Each title is therefore swapped for its
 * configured id where the tenant has mapped one (see config/erpat-labels).
 *
 * Titles without a mapping are sent as-is: the same behaviour as before, so an
 * unconfigured tenant is never made worse.
 */
function labelsCsv(tags: string[] | undefined): string {
  return (tags ?? [])
    .map((tag) => tag.trim())
    .filter(Boolean)
    .map((tag) => labelIdFor(tag) || tag)
    .join(',');
}

function toServerNote(payload: NoteWritePayload): Json {
  const body: Json = { title: payload.title };
  if (payload.body !== undefined) body.description = payload.body;
  if (payload.tags !== undefined) {
    body.labels = labelsCsv(payload.tags);
  }
  // The scene rides along ONLY with the picture it renders to — see the pairing
  // note on `NoteWritePayload.scene`. Sending it alone is a 422, so a caller
  // that forgot the body gets the note saved without its scene rather than an
  // error, and the drawing stays a flat picture.
  if (payload.scene !== undefined && payload.body !== undefined) {
    body.scribble_data = payload.scene === null ? null : sceneForWire(payload.scene);
  }
  // `type` is intentionally NOT included: it is immutable after creation and
  // PATCH rejects it outright. The create path adds it separately.
  return body;
}

/**
 * Put a scene through the same validator the server will, so what is sent is
 * byte-for-byte what comes back. Without this the two differ in the rounding of
 * every pen point and nothing can compare them.
 */
function sceneForWire(scene: SketchScene): Json {
  return (parseScene(scene) ?? scene) as unknown as Json;
}

/** Read the new note id from a create response (`{ note_id }` or `{ id }`). */
function newNoteId(data: unknown): string | number | null {
  const r = data && typeof data === 'object' ? (data as Json) : {};
  const v = r.note_id ?? r.id ?? (r.note as Json | undefined)?.id;
  return typeof v === 'number' || typeof v === 'string' ? v : null;
}

/**
 * Create a note with selected files. The SDK transport is JSON-only, so files
 * use a direct multipart request with the same auth + tenant headers as other
 * upload flows. Text-only notes keep the SDK path above.
 */
async function createMultipartNote(payload: NoteWritePayload): Promise<Json> {
  const form = new FormData();
  form.append('title', payload.title);
  form.append('type', payload.type ?? 'note');
  if (payload.body !== undefined) form.append('description', payload.body);
  if (payload.tags !== undefined) {
    form.append('labels', labelsCsv(payload.tags));
  }
  // The server accepts the scene as a JSON string here as readily as it accepts
  // an object on the JSON path, so a scribble with attachments keeps its shapes.
  if (payload.scene && payload.type === 'scribble' && payload.body !== undefined) {
    form.append('scribble_data', JSON.stringify(sceneForWire(payload.scene)));
  }
  for (const file of payload.files ?? []) {
    form.append('attachments[]', file, file.name);
  }

  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = erpatClient.getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const companyKey = getStoredCompanyKey();
  if (companyKey) headers['X-Company-Key'] = companyKey;

  let res: Response;
  try {
    // sessionFetch: a sign-out cancels an upload still in flight.
    res = await sessionFetch(`${API_BASE_URL}/v1/api/me/notes`, { method: 'POST', headers, body: form });
  } catch (err) {
    throw new ErpatApiError('network_error', err instanceof Error ? err.message : String(err), 0);
  }

  type MultipartNoteEnvelope = {
    ok?: boolean;
    data?: Json;
    error?: { code?: string; message?: string };
  };

  const text = await res.text();
  let envelope: MultipartNoteEnvelope | null = null;
  try {
    envelope = text ? JSON.parse(text) as MultipartNoteEnvelope : null;
  } catch {
    throw new ErpatApiError(`http_${res.status}`, 'Note upload response was not valid JSON.', res.status);
  }
  if (!res.ok || envelope?.ok === false) {
    const e = envelope?.error;
    throw new ErpatApiError(e?.code ?? `http_${res.status}`, e?.message ?? 'Note upload failed.', res.status);
  }
  return asRecord(envelope?.data);
}

export const notesService = {
  list: (query?: ListQuery, signal?: AbortSignal): Promise<NoteVM[]> =>
    unwrap(() => erpatClient.notes.list(query, signal)).then(mapNotes),
  // The "view" endpoint — returns one note's full detail (raw body kept for
  // rich rendering), mapped to a view model like the other read methods.
  get: (id: string, signal?: AbortSignal): Promise<NoteDetailVM> =>
    unwrap(() => erpatClient.notes.get(id, signal)).then(mapNoteDetail),
  create: async (payload: NoteWritePayload): Promise<Json> => {
    // The category is settable only here — it is what puts the note on the web
    // module's Notes / Scribbles / Journal tab, and PATCH can't change it later.
    const type = payload.type ?? 'note';
    const createBody: Json = { ...toServerNote(payload), type };
    // A drawing scene only applies to a scribble; on anything else the server
    // rejects the whole create with a 422 rather than ignoring the field.
    if (type !== 'scribble') delete createBody.scribble_data;
    const data = payload.files?.length
      ? await createMultipartNote(payload)
      : (await unwrap(() => erpatClient.notes.create(createBody))) as Json;
    const id = newNoteId(data);

    // `pinned` isn't honoured by create — apply it through the pin endpoint.
    if (payload.pinned && id != null) {
      await unwrap(() => erpatClient.notes.pin(id, { pinned: true }));
    }

    // Labels look to be dropped by create the same way `pinned` is: a note made
    // in Syntry arrives in ERPat with no label, even though the CSV is sent.
    // PATCH documents `labels`, so re-apply them to the new note — the kind
    // (Note / Meeting / Journal) IS one of those labels, and it is how ERPat
    // knows what the note is. Harmless if create did honour them: the same
    // value is written again.
    const labels = labelsCsv(payload.tags);
    if (id != null && labels) {
      try {
        await unwrap(() => erpatClient.notes.update(id, { labels }));
      } catch {
        // The note itself was created — never fail the save over its labels.
      }
    }

    return data;
  },
  update: (id: string, payload: NoteWritePayload): Promise<Json> =>
    unwrap(() => erpatClient.notes.update(id, toServerNote(payload))) as Promise<Json>,
  delete: (id: string): Promise<Json> => unwrap(() => erpatClient.notes.delete(id)) as Promise<Json>,
  pin: (id: string, pinned = true): Promise<Json> =>
    unwrap(() => erpatClient.notes.pin(id, { pinned })) as Promise<Json>,
};

// ── Todos ────────────────────────────────────────────────────────────────────
/**
 * The employee's focus session, as the SERVER holds it.
 *
 * Focus is exclusive per employee ACROSS clients: starting here stops and banks
 * whatever the ERPat web workspace had running, and the reverse. The server
 * derives elapsed time from its own stored start instant — no client sends a
 * duration, so no device can inflate a total, and the two clocks cannot drift
 * apart the way they did when each counted independently.
 */
export interface TodoFocusVM {
  /** The task being worked on, or '' when nothing is. */
  taskId: string;
  title: string;
  running: boolean;
  /** Seconds banked before the current run. */
  bankedSeconds: number;
  /** Banked plus the run in progress, as the SERVER computes it. */
  elapsedSeconds: number;
  /** UTC instant the current run began — render the live clock from this. */
  startedAtUtc: string;
  /**
   * The timed BLOCK, if the session was opened with one — ERPat 1.7.0's
   * "one session, two timers": the web renders the lifetime stopwatch, this app
   * renders what is left of the block, and both read one server row.
   * `null` when the session has no countdown.
   */
  sessionMinutes: number | null;
  /** Seconds spent inside THIS block, across any number of pause/resume cycles. */
  sessionConsumedSeconds: number;
  /** Derived server-side, never stored: `minutes*60 - consumed - since start`. */
  remainingSeconds: number | null;
}

function mapFocus(data: unknown): TodoFocusVM {
  const r = asRecord(domainBody(data, ['focus']));
  const id = r.id != null ? String(r.id) : '';
  return {
    taskId: id === '0' ? '' : id,
    title: typeof r.title === 'string' ? r.title : '',
    running: r.running === true,
    bankedSeconds: Math.max(0, Number(r.banked_seconds) || 0),
    elapsedSeconds: Math.max(0, Number(r.elapsed_seconds) || 0),
    startedAtUtc: typeof r.started_at_utc === 'string' ? r.started_at_utc : '',
    // `null` is meaningful here — "no countdown on this session" — so it is kept
    // apart from 0, which would read as a block that has just expired.
    sessionMinutes: nullableCount(r.session_minutes),
    sessionConsumedSeconds: Math.max(0, Number(r.session_consumed_seconds) || 0),
    remainingSeconds: nullableCount(r.remaining_seconds),
  };
}

/** A count the server may legitimately report as null. */
function nullableCount(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : null;
}

/**
 * Called through `erpatClient.request()` rather than a namespace: @erpat/sdk
 * 0.3.1 has no focus methods yet, and `request` is public precisely so a client
 * can reach an endpoint the SDK has not caught up with.
 */
export const todoFocusService = {
  /** What is running right now, for this employee, on any client. */
  get: (signal?: AbortSignal): Promise<TodoFocusVM> =>
    unwrap(() => erpatClient.request('/v1/api/me/todos/focus', { signal })).then(mapFocus),
  /**
   * Take the clock — stops and banks whatever else was running.
   *
   * `minutes` is the server's new-block-vs-resume switch, and the difference
   * matters: PRESENT opens a fresh block and resets the consumed counter,
   * ABSENT continues the block already on the task. Sending it on every start
   * would make a resume five minutes into a 25-minute block reopen at 25.
   */
  start: (id: string, minutes?: number): Promise<TodoFocusVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/todos/${encodeURIComponent(id)}/focus`, {
        method: 'POST',
        ...(minutes === undefined ? {} : { body: { minutes } }),
      }),
    ).then(mapFocus),
  /** Stop and bank the running session. */
  pause: (): Promise<TodoFocusVM> =>
    unwrap(() => erpatClient.request('/v1/api/me/todos/focus/pause', { method: 'POST' })).then(mapFocus),
};

export const todosService = {
  list: (query?: ListQuery, signal?: AbortSignal): Promise<Paged<Json>> =>
    unwrapList(() => erpatClient.todos.list(query, signal)),
  create: (body: Json): Promise<Json> => unwrap(() => erpatClient.todos.create(body)) as Promise<Json>,
  update: (id: string, body: Json): Promise<Json> =>
    unwrap(() => erpatClient.todos.update(id, body)) as Promise<Json>,
  delete: (id: string): Promise<Json> => unwrap(() => erpatClient.todos.delete(id)) as Promise<Json>,
};

// ── Support ──────────────────────────────────────────────────────────────────
export const supportChannelsService = {
  list: (query?: ListQuery, signal?: AbortSignal): Promise<SupportChannelVM[]> =>
    unwrap(() => erpatClient.supportChannels.list(query, signal)).then(mapSupportChannels),
};
/**
 * Which tickets `GET /me/tickets` returns:
 *   - `mine` (server default) — the ones the employee is personally on:
 *     requester, creator, assignee or collaborator.
 *   - `all` — everything their ticket permissions reach (a manager/agent's whole
 *     browse scope). Identical to `mine` for a plain employee.
 * Single-ticket reads and writes are always scope-wide, so a ticket a
 * notification pointed at can always be opened.
 */
export type TicketListScope = 'mine' | 'all';

export const ticketsService = {
  /**
   * Paginated (server-side, newest activity first). `unwrapList` keeps `meta`
   * so callers can see `pagination.total` — the plain `unwrap` dropped it and
   * every list silently saw only the first page.
   */
  list: (
    query?: ListQuery & { scope?: TicketListScope },
    signal?: AbortSignal,
  ): Promise<Paged<Ticket>> =>
    unwrapList(() => erpatClient.tickets.list(query, signal)).then((r) => ({
      items: mapTickets(r.items),
      pagination: r.pagination,
    })),
  get: (id: string, signal?: AbortSignal): Promise<Ticket> =>
    unwrap(() => erpatClient.tickets.get(id, signal)).then((d) => mapTicket(d)),
  /**
   * POST /me/tickets. Body: `ticket_type_id` (required unless the tenant offers
   * no types), `subject` (optional when "Hide ticket title" is on), `message`,
   * `priority`, `collaborators[]` (staff), `attachments[]` (tokens from
   * {@link ticketsService.upload}). See `buildCreateBody()` in views/ticketing.
   */
  create: (body: Json): Promise<Json> =>
    unwrap(() => erpatClient.request('/v1/api/me/tickets', { method: 'POST', body })) as Promise<Json>,
  /**
   * POST /me/tickets/{id}/messages expects { body } (not { message }), plus
   * `attachments` tokens. A reply may be text, attachments, or both. Returns
   * the new message plus the ticket's status + capabilities after the reply —
   * a staff assignee/collaborator/admin replying to a closed ticket REOPENS it
   * (mirrors the web save_comment()), and the response says so. `@[Name :id]`
   * mentions in the body notify the people mentioned, as on the web.
   */
  addMessage: (id: string, message: string, attachments?: string[]): Promise<TicketReplyResultVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/tickets/${encodeURIComponent(id)}/messages`, {
        method: 'POST',
        body: withAttachments({ body: message }, attachments),
      }),
    ).then(mapTicketReplyResult),
  /**
   * PATCH /me/tickets/{id} — the web Edit form: `title`, `ticket_type_id`,
   * `requested_by`, `collaborators[]`, `assigned_to`, `due_date` (local
   * `YYYY-MM-DD HH:MM`, '' clears). Only the keys sent change. The server
   * checks each against `capabilities.editFields`. Returns the updated ticket.
   */
  update: (id: string, body: Json): Promise<Ticket> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/tickets/${encodeURIComponent(id)}`, { method: 'PATCH', body }),
    ).then((d) => mapTicket(d)),
  /** GET /me/tickets/form-options — ticket types (with the web's label), hide-title, formats. */
  formOptions: (signal?: AbortSignal): Promise<TicketFormOptions> =>
    unwrap(() => erpatClient.request('/v1/api/me/tickets/form-options', { signal })).then(mapTicketFormOptions),
  /**
   * GET /me/tickets/people — the picker for a ticket form: `collaborator`
   * (ticket_collaborator reach), `requester` / `assignee` (ticket_staff reach).
   */
  people: (
    role: 'collaborator' | 'requester' | 'assignee',
    opts: { search?: string; page?: number; perPage?: number; signal?: AbortSignal } = {},
  ): Promise<TicketPerson[]> =>
    unwrap(() =>
      erpatClient.request('/v1/api/me/tickets/people', {
        query: {
          role,
          ...(opts.search ? { search: opts.search } : {}),
          page: opts.page ?? 1,
          per_page: opts.perPage ?? 25,
        },
        signal: opts.signal,
      }),
    ).then(mapTicketPeople),
  /** POST /me/tickets/uploads — stage one attachment; the token goes in `attachments`. */
  upload: (file: File): Promise<MessageUploadResult> => uploadMessageAttachment('/v1/api/me/tickets/uploads', file),
  /** GET /me/tickets/{id}/mentionables — who a reply may @mention, with the token to insert. */
  mentionables: (id: string, signal?: AbortSignal): Promise<TicketMentionable[]> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/tickets/${encodeURIComponent(id)}/mentionables`, { signal }),
    ).then(mapTicketMentionables),
  /** GET /me/tickets/{id}/templates — the "Insert template" picker (ticket managers). */
  templates: (id: string, opts: { search?: string; signal?: AbortSignal } = {}): Promise<TicketTemplateOption[]> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/tickets/${encodeURIComponent(id)}/templates`, {
        query: { per_page: 100, ...(opts.search ? { search: opts.search } : {}) },
        signal: opts.signal,
      }),
    ).then(mapTicketTemplates),
  /** GET /me/tickets/knowledge-base — the "Insert knowledge base" picker (ticket managers). */
  knowledgeBase: (opts: { search?: string; signal?: AbortSignal } = {}): Promise<TicketKbOption[]> =>
    unwrap(() =>
      erpatClient.request('/v1/api/me/tickets/knowledge-base', {
        query: { per_page: 100, ...(opts.search ? { search: opts.search } : {}) },
        signal: opts.signal,
      }),
    ).then(mapTicketKbOptions),
  /**
   * A freshly signed link to one attachment of a ticket message — what the file
   * viewer's re-sign hook and Download use (an S3 link expires). `download: true`
   * signs it to save under the file's name.
   */
  fileLink: (
    id: string,
    messageId: string,
    index: number,
    opts: { download?: boolean; signal?: AbortSignal } = {},
  ): Promise<string> =>
    unwrap(() =>
      erpatClient.request(
        `/v1/api/me/tickets/${encodeURIComponent(id)}/messages/${encodeURIComponent(messageId)}/files/${encodeURIComponent(String(index))}`,
        { query: opts.download ? { download: 1 } : undefined, signal: opts.signal },
      ),
    ).then(mapTicketFileLink),
  /**
   * POST /me/tickets/{id}/status — reopen / on hold / close, in ERPat's raw
   * enum. The server enforces `capabilities.allowedTransitions`; a value
   * outside it is a 422 `invalid_transition`. Returns the updated ticket.
   * Via the public request() helper: the vendored SDK has no wrapper for it.
   */
  changeStatus: (id: string, status: TicketRawStatus): Promise<Ticket> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/tickets/${encodeURIComponent(id)}/status`, {
        method: 'POST',
        body: { status },
      }),
    ).then((d) => mapTicket(d)),
  /**
   * POST /me/tickets/{id}/assign-to-me — claim an unassigned new ticket (opens
   * it). 409 `cannot_assign` once someone holds it. Returns the updated ticket.
   */
  assignToMe: (id: string): Promise<Ticket> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/tickets/${encodeURIComponent(id)}/assign-to-me`, {
        method: 'POST',
        body: {},
      }),
    ).then((d) => mapTicket(d)),
};

// ── Knowledge Base (help articles) ───────────────────────────────────────────
// The end-user help-articles endpoint is list-only (no detail route) and returns
// each article's full body inline, so `get` resolves against the last fetched
// list — refetching only on a cold deep-link / reload when the cache is empty.
let kbCache: KbArticleVM[] = [];
export const knowledgeBaseService = {
  list: (query?: ListQuery, signal?: AbortSignal): Promise<KbArticleVM[]> =>
    unwrap(() => erpatClient.helpArticles.list(query, signal)).then((d) => {
      kbCache = mapKbArticles(d);
      return kbCache;
    }),
  get: async (id: string, signal?: AbortSignal): Promise<KbArticleVM | null> => {
    const hit = kbCache.find((a) => a.id === id);
    if (hit) return hit;
    const list = await knowledgeBaseService.list(undefined, signal);
    return list.find((a) => a.id === id) ?? null;
  },
};

// ── Employee Handbook (Helpdesk → Handbook pillar) ───────────────────────────
/**
 * The company handbook: server-owned chapters over published sections.
 *
 * Backed by `modules/Helpdesk/controllers/Handbook_euapi.php`
 * (`/v1/api/me/handbook/*`). Not in `@erpat/sdk`, so every call goes through the
 * `request()` escape hatch — the same pattern as `teamAttendanceService`.
 *
 * Unlike `knowledgeBaseService` above, this endpoint set has a REAL detail route
 * and SQL paging, so there is deliberately NO client cache shim here: `get()`
 * fetches the section (which is also what increments its view counter server-side).
 *
 * Search is likewise SERVER-side. It has to be: list rows carry no `body`, and the
 * server matches title + excerpt + body — a client-side filter over the loaded page
 * would silently miss every section whose match is in the body, which for a
 * handbook is most of them.
 */
export interface HandbookArticleQuery {
  category_id?: number | string;
  search?: string;
  /** `recent` = newest published first; omit for the curated handbook order. */
  order?: 'recent';
  page?: number;
  per_page?: number;
}

export const handbookService = {
  /** Every chapter + the whole-handbook summary (counts, last-updated). */
  summary: (signal?: AbortSignal): Promise<HandbookSummaryVM> =>
    unwrap(() => erpatClient.request('/v1/api/me/handbook/categories', { signal })).then((d) =>
      mapHandbookSummary(d),
    ),

  /** One page of published sections. Rows carry NO body — use `get()` to read one. */
  articles: (
    query: HandbookArticleQuery = {},
    signal?: AbortSignal,
  ): Promise<{ items: HandbookArticleVM[]; pagination: Pagination }> =>
    unwrapList(() =>
      erpatClient.request('/v1/api/me/handbook/articles', {
        query: query as Record<string, QueryValue>,
        signal,
      }),
    ).then((res) => ({
      items: res.items.map((a, i) => mapHandbookArticle(a, i)),
      pagination: res.pagination,
    })),

  /**
   * One published section, body included. Returns `null` when the section does
   * not exist / is not published — the server says so with a 404 carrying the
   * specific `handbook_article_not_found` code, which lets a genuinely missing
   * section render a clean "not found" while a module-off 404
   * (`module_unavailable`) still propagates and shows the unavailable state.
   */
  get: async (id: string, signal?: AbortSignal): Promise<HandbookArticleVM | null> => {
    try {
      const d = await unwrap(() =>
        erpatClient.request(`/v1/api/me/handbook/articles/${encodeURIComponent(id)}`, { signal }),
      );
      return mapHandbookArticle(d);
    } catch (err) {
      if (err instanceof ErpatApiError && err.code === 'handbook_article_not_found') return null;
      throw err;
    }
  },
};

// ── Forms (answerable Marketing forms) ───────────────────────────────────────
/**
 * Org-wide answerable forms for the Syntry app. Backed by the existing end-user
 * API (`application/controllers/v1/api/marketing/Forms.php`), NOT in `@erpat/sdk`
 * yet — so reads/writes go through the SDK `request()` escape hatch:
 *   GET  /v1/api/forms                  — list answerable (open/closed) forms
 *   GET  /v1/api/forms/{uuid}           — one form + its fields (the questions)
 *   POST /v1/api/forms/{uuid}/responses — submit answers (JSON)
 *   POST /v1/api/forms/{uuid}/uploads   — stage a file answer (multipart) → token
 *
 * Listing/viewing require only the tenant's `forms` module; answering requires
 * the `forms_submission_create` permit (enforced server-side). A disabled module
 * 404s and a missing permit 403s — useAsyncData maps both to "unavailable".
 */
export interface FormListQuery {
  /** open (default) | closed | all — drafts are never exposed. */
  status?: 'open' | 'closed' | 'all';
  search?: string;
  page?: number;
  per_page?: number;
}

/** answers keyed by custom-field id; scalar for most types, string[] for multi_select / file tokens. */
export type FormAnswerMap = Record<string, string | string[]>;

export interface FormSubmitPayload {
  answers: FormAnswerMap;
  /** Staged upload tokens for a form-level (global) attachment, if enabled. */
  attachments?: string[];
}

/** A staged file answer — `token` is passed back in the submission's answers/attachments. */
export interface FormUploadResult {
  token: string;
  name: string;
  size: number;
}

/**
 * Upload one file answer via multipart ({@link postMultipartFile}, the shared
 * upload transport). Returns the staged token to submit.
 */
async function uploadFormFile(uuid: string, file: File): Promise<FormUploadResult> {
  const data = await postMultipartFile(`/v1/api/forms/${encodeURIComponent(uuid)}/uploads`, file, 'Upload failed.');
  const fileData = asRecord(data.file);
  return {
    token: String(fileData.token ?? ''),
    name: String(fileData.name ?? file.name),
    size: typeof fileData.size === 'number' ? fileData.size : file.size,
  };
}

/** `/v1/api/forms`: "Items per page. Default 25, max 100." — the ceiling to page at. */
const FORMS_MAX_PAGE_SIZE = 100;
/** Safety stop for the paging walk — far past any real forms catalog. */
const FORMS_MAX_PAGES = 20;

export const formsService = {
  /** ONE page of forms — the server's default of 25 unless `per_page` says otherwise. */
  list: (query?: FormListQuery, signal?: AbortSignal): Promise<FormSummaryVM[]> =>
    unwrap(() =>
      erpatClient.request('/v1/api/forms', { query: query as Record<string, QueryValue> | undefined, signal }),
    ).then(mapForms),
  /**
   * EVERY answerable form, walked page by page.
   *
   * The Forms list page derives its Total/Open/Public tiles, AND its search box
   * (which matches on creator/dates/sysId too — richer than the server's
   * documented title+description `search`), from the full set client-side. A
   * single page — even at the max `per_page: 100` — would under-report the
   * tiles and make search silently miss any older form past page one.
   *
   * Rows are collected raw and mapped ONCE at the end: `mapFormSummary` falls
   * back to the row INDEX for an id-less row, so mapping page by page would
   * restart that counter and collide ids across pages.
   */
  listAll: async (query?: FormListQuery, signal?: AbortSignal): Promise<FormSummaryVM[]> => {
    const rows: Json[] = [];

    for (let page = 1; page <= FORMS_MAX_PAGES; page += 1) {
      const result = await unwrapList<Json>(() =>
        erpatClient.request('/v1/api/forms', {
          query: { ...query, page, per_page: FORMS_MAX_PAGE_SIZE } as Record<string, QueryValue>,
          signal,
        }),
      );
      if (!result.items.length) break;
      rows.push(...result.items);

      // The server echoes the page it served. If that isn't the page we asked
      // for — it ignored the param, or sent no meta at all — asking again would
      // just re-fetch the same rows, so stop with what we have.
      if (result.pagination.page !== page) break;
      if (result.pagination.total > 0 && rows.length >= result.pagination.total) break;
      // A short page is the last page, measured against the size the server
      // actually served (which may be below the one we requested).
      const served = result.pagination.perPage > 0 ? result.pagination.perPage : FORMS_MAX_PAGE_SIZE;
      if (result.items.length < served) break;
    }

    return mapForms(rows);
  },
  get: (uuid: string, signal?: AbortSignal): Promise<FormDetailVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/forms/${encodeURIComponent(uuid)}`, { signal }),
    ).then(mapFormDetail),
  submit: (uuid: string, payload: FormSubmitPayload): Promise<Json> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/forms/${encodeURIComponent(uuid)}/responses`, {
        method: 'POST',
        body: payload,
      }),
    ) as Promise<Json>,
  upload: (uuid: string, file: File): Promise<FormUploadResult> => uploadFormFile(uuid, file),
};

// ── Surveys (in-app survey / consent prompts) ────────────────────────────────
/**
 * The signed-in employee's pending survey/consent queue, served by the ERPat
 * Surveys module (`modules/Surveys/controllers/Survey_prompt_euapi.php`). Not in
 * `@erpat/sdk` yet — reads/writes go through the `request()` escape hatch:
 *   GET  /v1/api/me/surveys          — pending queue (max 5, most-urgent first,
 *                                      questions inline)
 *   POST /v1/api/me/surveys/respond  — record answer / agree / decline / snooze /
 *                                      opt_out for one survey token
 *
 * No permit is required (self-only). The surface is gated on the Surveys module
 * AND its mobile-prompts policy switch — either off answers 404
 * `module_unavailable`, which useAsyncData-style callers treat as "the feature
 * does not exist" (stay silent, never error).
 *
 * ⚠ Fetching RECORDS AN IMPRESSION against the queue head server-side (it counts
 * as one "ask" toward an optional survey's max_prompts). Only the surveys store
 * may call `pending()` — it throttles and fetches only when about to present.
 * Respond error contract: 422 `validation_failed` (details.fields keyed by
 * question id), 404 `survey_not_found` (no longer pending — e.g. answered on
 * another device), 409 `action_not_allowed` (snooze/opt_out on a required
 * survey). A duplicate submit is a SUCCESS (`data.status = "already_recorded"`).
 * The backend owns ALL pacing state (snooze / impressions / opt-out) — never
 * persist dismissal state locally.
 */
export type SurveyRespondAction = 'answer' | 'agree' | 'decline' | 'snooze' | 'opt_out';

/** Answers keyed by question id; scalar string for most types, string[] for multi_choice. */
export type SurveyAnswerMap = Record<string, string | string[]>;

export const surveysService = {
  pending: (signal?: AbortSignal): Promise<SurveyPromptVM[]> =>
    unwrap(() => erpatClient.request('/v1/api/me/surveys', { signal })).then(mapSurveyQueue),
  respond: (token: string, action: SurveyRespondAction, answers?: SurveyAnswerMap): Promise<Json> =>
    unwrap(() =>
      erpatClient.request('/v1/api/me/surveys/respond', {
        method: 'POST',
        body: { survey: token, action, ...(answers ? { answers } : {}) },
      }),
    ) as Promise<Json>,
};

// ── Misc read-only feeds ─────────────────────────────────────────────────────
export const timelineService = {
  list: (query?: ListQuery, signal?: AbortSignal): Promise<Paged<Json>> =>
    unwrapList(() => erpatClient.timeline.list(query, signal)),
  /** The activity feed mapped to view models (root posts), with pagination. */
  feed: (query?: ListQuery, signal?: AbortSignal): Promise<Paged<TimelinePostVM>> =>
    unwrapList(() => erpatClient.timeline.list(query, signal)).then((res) => ({
      items: mapTimelinePosts(res.items),
      pagination: res.pagination,
    })),
  get: (id: string, signal?: AbortSignal): Promise<Json> =>
    unwrap(() => erpatClient.timeline.get(id, signal)) as Promise<Json>,
  /** One post + its comments, mapped to view models (GET /me/timeline/{id}). */
  detail: (id: string, signal?: AbortSignal): Promise<TimelinePostDetailVM> =>
    unwrap(() => erpatClient.timeline.get(id, signal)).then(mapTimelinePostDetail),
  create: (body: Json): Promise<Json> => unwrap(() => erpatClient.timeline.create(body)) as Promise<Json>,
  comment: (id: string, body: string): Promise<Json> =>
    // POST /me/timeline/{id}/comments expects { description } (not { body }) — verified against openapi.json.
    unwrap(() => erpatClient.timeline.comment(id, { description: body })) as Promise<Json>,
  /**
   * Toggle a reaction on a post and return the fresh reaction summary.
   * `reaction` defaults server-side to "support" (a generic like); re-sending
   * the same reaction removes it. Not in the SDK yet — uses request().
   */
  react: (id: string, reaction?: string, signal?: AbortSignal): Promise<TimelineReactionsVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/timeline/${encodeURIComponent(id)}/reactions`, {
        method: 'POST',
        body: reaction ? { reaction } : {},
        signal,
      }),
    ).then(mapTimelineReactionResponse),
};
export const holidaysService = {
  list: (query?: ListQuery, signal?: AbortSignal): Promise<Paged<Json>> =>
    unwrapList(() => erpatClient.holidays.list(query, signal)),
};

// ── Celebrations (company-wide birthdays + work anniversaries) ──────────────
/**
 * This month's celebrants across the whole company, for the Home widgets.
 *
 *   GET /v1/api/me/celebrations/birthdays
 *   GET /v1/api/me/celebrations/anniversaries
 *
 * Company-wide (every active staff member) and PII-light by design: month/day
 * plus a preformatted label, never a birth year and never an age. Both
 * responses also carry the tenant's canned greeting strings, so the Greet
 * action can pre-fill a post without the app keeping a copy that would drift
 * from the server's.
 *
 * Neither endpoint is in `@erpat/sdk` yet, so both go through the low-level
 * `request()` escape hatch (same pattern as `appDashboardService`). A 403/404
 * means the feature is off for this tenant/account — the widgets latch that and
 * hide themselves entirely.
 */

/**
 * The server's MAX_LIMIT. Without an explicit `limit` it defaults to 25, which
 * was fine when the scope was one team but truncates a company-wide month (a
 * 575-staff tenant averages ~48 birthdays a month). Asked for up front so the
 * "View Celebrants" sheet can render the full list from data the widget already
 * holds, instead of firing a second request when it opens — the rows are seven
 * short fields each, so 100 of them is a few KB. `total` is always the
 * UNLIMITED count, so the chip and the sheet's "+N more" stay honest past 100.
 */
const CELEBRANTS_LIMIT = 100;

export const celebrationsService = {
  birthdays: (signal?: AbortSignal): Promise<CelebrationsVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/celebrations/birthdays?limit=${CELEBRANTS_LIMIT}`, {
        signal,
      }),
    ).then(mapBirthdayCelebrants),
  anniversaries: (signal?: AbortSignal): Promise<CelebrationsVM<AnniversaryCelebrantVM>> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/celebrations/anniversaries?limit=${CELEBRANTS_LIMIT}`, {
        signal,
      }),
    ).then(mapAnniversaryCelebrants),
};

/**
 * Events.
 *
 * Every call here is STRICT: one path, no fallback ladder. The ladder that used
 * to live here existed only while `/v1/api/me/events` was not yet deployed, and
 * keeping it now would mask a real 404 — a mistyped id, an event this user
 * cannot see, a module switched off — as a silent empty list.
 */

/**
 * The list query.
 *
 * Both date bounds are REQUIRED. The endpoint treats them as both-or-neither,
 * and no screen in this app wants a company's entire event history: every
 * caller already has a window in mind, so the type makes it say so.
 *
 * Written as a `type` and not an `interface` on purpose — only a type alias
 * gets the implicit index signature that makes it assignable to `ListQuery`.
 */
export type EventListQuery = {
  /** `YYYY-MM-DD`, inclusive. */
  start_date: string;
  /** `YYYY-MM-DD`, inclusive. */
  end_date: string;
  /** Ask the server to expand a recurring series into its occurrences. */
  include_recurring?: boolean;
  order?: 'asc' | 'desc';
  page?: number;
  /** Max 100; the server defaults to 25. */
  per_page?: number;
};

const EVENTS_BASE = '/v1/api/me/events';

export const eventsService = {
  /**
   * A page of events.
   *
   * With `include_recurring` the server expands a series into occurrences, so
   * `meta.total` counts BASE rows while the page carries OCCURRENCES: a page can
   * hold more entries than `per_page`, and several of them can share one `id`.
   * Key and dedupe on `EventVM.key` — never on `id`.
   */
  list: async (query: EventListQuery, signal?: AbortSignal): Promise<Paged<EventVM>> => {
    const params: ListQuery = {
      start_date: query.start_date,
      end_date: query.end_date,
      include_recurring: query.include_recurring,
      order: query.order,
      page: query.page,
      per_page: query.per_page,
    };
    const { items, pagination } = await unwrapList(() => erpatClient.events.list(params, signal));
    return { items: mapEvents(items), pagination };
  },

  /** One event with its agenda and files. 404 `event_not_found` when not visible. */
  get: (id: string, signal?: AbortSignal): Promise<EventDetailVM> =>
    unwrap(() => erpatClient.request(`${EVENTS_BASE}/${encodeURIComponent(id)}`, { signal })).then(mapEventDetail),

  /**
   * Answer an RSVP.
   *
   * Only `going` / `not_going` are ever sent. The server also accepts the legacy
   * `confirmed` / `rejected` and deliberately does not get them from here: one
   * outbound vocabulary is what keeps the request and the optimistic patch in
   * step. 409 `rsvp_disabled` / `rsvp_closed` are real answers — surface them.
   */
  respond: (id: string, status: RsvpStatus, signal?: AbortSignal): Promise<RsvpResultVM> =>
    unwrap(() => erpatClient.events.respond(id, { status }, signal)).then(mapRsvpResult),

  /**
   * The going / not-going roster, visible to anyone the event is shared with.
   * An RSVP-disabled event answers 200 with an empty list, not an error.
   */
  attendees: async (
    id: string,
    query?: { status?: string; page?: number; per_page?: number },
    signal?: AbortSignal,
  ): Promise<Paged<AttendeeVM>> => {
    const { items, pagination } = await unwrapList(() =>
      erpatClient.request(`${EVENTS_BASE}/${encodeURIComponent(id)}/attendees`, {
        query: { status: query?.status, page: query?.page, per_page: query?.per_page },
        signal,
      }),
    );
    return { items: mapAttendees(items), pagination };
  },

  /**
   * The reminder lead — a PER-USER GLOBAL held server-side, not a per-event
   * setting, so it is read and written once for the whole module.
   */
  reminderPreference: {
    get: (signal?: AbortSignal): Promise<ReminderLead> =>
      unwrap(() => erpatClient.request(`${EVENTS_BASE}/reminder-preference`, { signal })).then(mapReminderLead),
    /** `lead` must be `20m` / `1d` / `off`; anything else is a 422. */
    update: (lead: ReminderLead): Promise<ReminderLead> =>
      unwrap(() =>
        erpatClient.request(`${EVENTS_BASE}/reminder-preference`, { method: 'PATCH', body: { lead } }),
      ).then(mapReminderLead),
  },
};

export const teamsService = {
  list: (query?: ListQuery, signal?: AbortSignal): Promise<Paged<Json>> =>
    unwrapList(() => erpatClient.teams.list(query, signal)),
  members: (id: string, query?: ListQuery, signal?: AbortSignal): Promise<Paged<Json>> =>
    unwrapList(() => erpatClient.teams.members(id, query, signal)),
};

/**
 * Org directory for the Departments page. Lists every team/department/group the
 * signed-in user belongs to (GET /me/teams) and enriches each with its members
 * (GET /me/teams/{id}/members) so a card can show the head + member avatars.
 *
 * Members load per-team in parallel; a team whose members can't be read (e.g. a
 * 404 for a unit the caller can't enumerate) degrades to an empty roster instead
 * of failing the whole page. `mapDepartment` derives the unit type + head, which
 * the end-user API doesn't expose explicitly.
 */
export const departmentsService = {
  list: async (signal?: AbortSignal): Promise<DepartmentVM[]> => {
    const { items } = await teamsService.list(undefined, signal);
    return Promise.all(
      items.map(async (team, i) => {
        const id = String(pick<unknown>(team, ['id', 'uuid', '_id', 'key'], i));
        const members = await teamsService
          .members(id, undefined, signal)
          .then((res) => res.items)
          .catch(() => [] as Json[]);
        return mapDepartment(team, members, i);
      }),
    );
  },
};
/**
 * People-photo lookups for lists whose own payload has no avatar.
 *
 * The team-attendance rows (`/v1/api/attendance`) and the compose contacts
 * (`/me/contacts`) carry NO photo field at all, and the team roster's `image`
 * comes back empty for tenants that store photos elsewhere — so those screens
 * fall back to initials no matter how defensively they are mapped.
 */
/** Lower-cased, whitespace-collapsed name — the key for the name-based join. */
export function normalizePersonName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

export const directoryService = {
  /**
   * One user's avatar via `GET /v1/api/users/{id}/profile` — the same envelope
   * as `/me/profile` (so {@link mapProfile} reads it), and the only surface that
   * resolves a photo for ANY colleague rather than just teammates. It returns an
   * absolute URL, e.g. "https://erpat.app/files/profile/avatar34.png".
   *
   * Access is scoped to the caller's messaging contacts, so a target outside
   * that scope 404s (existence is hidden, never 403) — callers treat any
   * failure as "no photo". `userId` must be a numeric `users.id`; the avatars
   * store filters everything else out before calling this.
   */
  userAvatar: async (userId: string, signal?: AbortSignal): Promise<string> =>
    (await profileService.getUser(userId, signal)).image,

  /**
   * Bulk `userId -> avatar URL` for the caller's own teams, from `GET /me/teams`
   * + `GET /me/teams/{id}/members` (which documents an `image`). One pass covers
   * every teammate; a per-team fetch that fails is skipped rather than failing
   * the whole directory.
   */
  avatars: async (signal?: AbortSignal): Promise<Record<string, string>> => {
    const { items: teams } = await teamsService.list(undefined, signal);
    const directory: Record<string, string> = {};

    await Promise.all(
      teams.map(async (team, i) => {
        const teamId = String(pick<unknown>(team, ['id', 'uuid', '_id', 'key'], i));
        const members = await teamsService
          .members(teamId, undefined, signal)
          .then((res) => res.items)
          .catch(() => [] as Json[]);

        for (const member of mapGroupMembers(members)) {
          if (!member.image) continue;
          if (member.id) directory[member.id] = member.image;
          // Also key by name: a row elsewhere may carry an EMPLOYEE id rather
          // than a user id (the attendance surface nests its own `employee`
          // record), and the name is the only field both sides always share.
          if (member.name) directory[`name:${normalizePersonName(member.name)}`] = member.image;
        }
      }),
    );

    return directory;
  },
};

/** `EuapiPageMeta`: "per_page default 25, max 100" — the ceiling to page at. */
const DISCIPLINARY_MAX_PAGE_SIZE = 100;
/** Safety stop for the paging walk — far past any real case history. */
const DISCIPLINARY_MAX_PAGES = 20;

export const disciplinaryService = {
  /**
   * EVERY case, walked page by page.
   *
   * `GET /me/disciplinary` is paginated newest-first at 25 per page. A caller
   * that filters the result CLIENT-side — the records page narrows by week,
   * month or year, and derives its stat tiles and status chips from what is
   * left — would otherwise report "no records" for any period older than the
   * first page, with nothing to say the rest was never fetched. There is no
   * single-page variant here because nothing needs one; add it back the day a
   * caller genuinely wants one page.
   *
   * Rows are collected raw and mapped ONCE at the end: `mapDisciplinary` falls
   * back to the row INDEX for an id-less row, so mapping page by page would
   * restart that counter and collide ids across pages.
   */
  listAll: async (query?: ListQuery, signal?: AbortSignal): Promise<Paged<DisciplinaryCaseVM>> => {
    const rows: Json[] = [];
    let pagination: Pagination = { page: 1, perPage: DISCIPLINARY_MAX_PAGE_SIZE, total: 0, totalPages: 1 };

    for (let page = 1; page <= DISCIPLINARY_MAX_PAGES; page += 1) {
      const result = await unwrapList<Json>(() =>
        erpatClient.disciplinary.list({ ...query, page, per_page: DISCIPLINARY_MAX_PAGE_SIZE }, signal),
      );
      pagination = result.pagination;
      if (!result.items.length) break;
      rows.push(...result.items);

      // The server echoes the page it served. If that isn't the page we asked
      // for — it ignored the param, or sent no meta at all — asking again would
      // just re-fetch the same rows, so stop with what we have.
      if (pagination.page !== page) break;
      if (pagination.total > 0 && rows.length >= pagination.total) break;
      // A short page is the last page, measured against the size the server
      // actually served (which may be below the one we requested).
      const served = pagination.perPage > 0 ? pagination.perPage : DISCIPLINARY_MAX_PAGE_SIZE;
      if (result.items.length < served) break;
    }

    return {
      items: rows.map((item, index) => mapDisciplinary(item, index)),
      pagination: {
        ...pagination,
        page: 1,
        perPage: rows.length,
        total: Math.max(pagination.total, rows.length),
        totalPages: 1,
      },
    };
  },
  /** GET /me/disciplinary/{id} — the case with its thread, `capabilities`, acknowledgement and corrective actions. */
  get: (id: string, signal?: AbortSignal): Promise<DisciplinaryCaseDetailVM> =>
    unwrap(() => erpatClient.disciplinary.get(id, signal)).then((data) => mapDisciplinaryDetail(data)),
  /**
   * POST /me/disciplinary/{id}/reply — Compliance 1.6.0's one reply route (201): a free-text
   * reply with its visibility (public unless asked otherwise) or one of the configured quick
   * replies, sent as `{ prefab }` alone. Answers the case as the server re-read it. ONE request,
   * whatever it answers: a 404 is the case being gone, not a cue to try another path.
   */
  reply: (id: string, reply: DisciplinaryReply): Promise<DisciplinaryCaseDetailVM> => {
    const body =
      'prefab' in reply ? { prefab: reply.prefab } : { body: reply.body.trim(), visibility: reply.visibility ?? 'public' };
    return unwrap(() =>
      erpatClient.request(`/v1/api/me/disciplinary/${encodeURIComponent(id)}/reply`, { method: 'POST', body }),
    ).then((data) => mapDisciplinaryDetail(data));
  },
  /**
   * POST /me/disciplinary/{id}/acknowledge — no body, once (201; again: 409
   * `acknowledgement_unavailable`). The answer may be the case closed: the last
   * acknowledgement closes it.
   */
  acknowledge: (id: string): Promise<DisciplinaryCaseDetailVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/disciplinary/${encodeURIComponent(id)}/acknowledge`, { method: 'POST', body: {} }),
    ).then((data) => mapDisciplinaryDetail(data)),
};

// ── Compliance self-service (Investigations, Corrective Actions) ─────────────
// ERPat Compliance 1.6.0's End-User API; the contract is modules/Compliance/specs/
// employee-self-service-implementation-plan.md §3.3–§3.4. None of these routes has
// an SDK wrapper, so each goes through the public request() helper. Every write
// answers the record as the server re-read it; what the caller may do is the
// server's `capabilities`, read by the mappers and nowhere derived. Failures are
// worded by `complianceRefusal` (services/compliance-refusals.ts).

/** The investigations the caller LEADS (and only those). */
export const investigationsService = {
  /** GET /me/investigations — one page (`phase` CSV, `search`), open first. */
  list: (query: InvestigationQuery = {}, signal?: AbortSignal): Promise<Paged<InvestigationVM>> =>
    unwrap(() =>
      erpatClient.request('/v1/api/me/investigations', {
        method: 'GET',
        query: investigationListParams(query),
        signal,
      }),
    ).then((data) => mapInvestigationPage(data, query.page ?? 1)),
  /** GET /me/investigations/{id} — with its corrective actions, notes and `capabilities`. */
  get: (id: string, signal?: AbortSignal): Promise<InvestigationDetail> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/investigations/${encodeURIComponent(id)}`, { method: 'GET', signal }),
    ).then((data) => mapInvestigationDetail(data)),
  /** PATCH /me/investigations/{id} — the lead's edits; only the keys in `patch` are sent. */
  update: (id: string, patch: InvestigationPatch): Promise<InvestigationDetail> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/investigations/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch }),
    ).then((data) => mapInvestigationDetail(data)),
  /** POST /me/investigations/{id}/notes — the lead's note, as trimmed plain text. */
  addNote: (id: string, body: string): Promise<InvestigationDetail> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/investigations/${encodeURIComponent(id)}/notes`, {
        method: 'POST',
        body: { body: body.trim() },
      }),
    ).then((data) => mapInvestigationDetail(data)),
};

/** The corrective actions the caller OWNS or that are ABOUT them. */
export const correctiveActionsService = {
  /** GET /me/corrective-actions — one page (`role`, `status` CSV, `linked_case_id`, `search`), open first. */
  list: (query: CorrectiveActionQuery = {}, signal?: AbortSignal): Promise<Paged<CorrectiveActionVM>> =>
    unwrap(() =>
      erpatClient.request('/v1/api/me/corrective-actions', {
        method: 'GET',
        query: correctiveActionListParams(query),
        signal,
      }),
    ).then((data) => mapCorrectiveActionPage(data, query.page ?? 1)),
  /** GET /me/corrective-actions/{id} — with its notes and `capabilities`. */
  get: (id: string, signal?: AbortSignal): Promise<CorrectiveActionDetail> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/corrective-actions/${encodeURIComponent(id)}`, { method: 'GET', signal }),
    ).then((data) => mapCorrectiveActionDetail(data)),
  /** PATCH /me/corrective-actions/{id} — the owner's status and dates; only the keys in `patch` are sent. */
  update: (id: string, patch: CorrectiveActionPatch): Promise<CorrectiveActionDetail> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/corrective-actions/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch }),
    ).then((data) => mapCorrectiveActionDetail(data)),
  /** POST /me/corrective-actions/{id}/acknowledge — the employee, once; no content in the body. */
  acknowledge: (id: string): Promise<CorrectiveActionDetail> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/corrective-actions/${encodeURIComponent(id)}/acknowledge`, {
        method: 'POST',
        body: {},
      }),
    ).then((data) => mapCorrectiveActionDetail(data)),
  /** POST /me/corrective-actions/{id}/notes — the owner's or the employee's note, as trimmed plain text. */
  addNote: (id: string, body: string): Promise<CorrectiveActionDetail> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/corrective-actions/${encodeURIComponent(id)}/notes`, {
        method: 'POST',
        body: { body: body.trim() },
      }),
    ).then((data) => mapCorrectiveActionDetail(data)),
};

export const sessionsService = {
  list: (query?: ListQuery, signal?: AbortSignal): Promise<Paged<Json>> =>
    unwrapList(() => erpatClient.sessions.list(query, signal)),
  revoke: (id: string): Promise<Json> => unwrap(() => erpatClient.sessions.revoke(id)) as Promise<Json>,
};

// ── Activity (the caller's own system-log trail) ─────────────────────────────
/** Filters accepted by GET /me/activity. Blank values are dropped, not sent. */
export interface ActivityQuery {
  page?: number;
  per_page?: number;
  /** Free text over the title, remarks and raw action/component keys (max 100). */
  search?: string;
  /** A component `key` from {@link activityService.summary}. */
  module?: string;
  /** Inclusive tenant-local dates, `YYYY-MM-DD`. */
  start_date?: string;
  end_date?: string;
}

/**
 * The signed-in user's OWN activity — their `system_logs` rows. Self-scoped by
 * the server (JWT subject only). Not in `@erpat/sdk` yet, so both calls use the
 * `request()` escape hatch. A 404 `module_unavailable` means the tenant turned
 * System Logs off; `useAsyncData.unavailable` renders that as a calm state.
 */
export const activityService = {
  /** One page, newest first. `pagination.total` is a real COUNT of the filtered set. */
  list: (query: ActivityQuery = {}, signal?: AbortSignal): Promise<Paged<ActivityEntryVM>> => {
    const params: Record<string, QueryValue> = {};
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      if (typeof value === 'string' && value.trim() === '') continue;
      params[key] = typeof value === 'string' ? value.trim() : value;
    }
    return unwrapList<Json>(() => erpatClient.request('/v1/api/me/activity', { query: params, signal })).then(
      ({ items, pagination }) => ({ items: items.map((item, i) => mapActivityEntry(item, i)), pagination }),
    );
  },
  /** Totals + the per-module breakdown that drives the filter chips. */
  summary: (signal?: AbortSignal): Promise<ActivitySummaryVM> =>
    unwrap(() => erpatClient.request('/v1/api/me/activity/summary', { signal })).then(mapActivitySummary),
};
/** Result of POST /me/app-rating — the server returns the new rating's id. */
export interface AppRatingResult {
  rating_id?: number;
}
export const appRatingService = {
  /**
   * Submit an in-app rating (1–5 stars) with optional free-text feedback.
   * Every submission is enriched with the app version + platform (both accepted
   * by POST /me/app-rating) so reviews are attributable to a build. Empty
   * feedback is omitted rather than sent as a blank string.
   */
  submit: (stars: number, feedback?: string): Promise<AppRatingResult> => {
    const body: Json = { stars, app_version: APP_VERSION, platform: detectPlatform() };
    const trimmed = feedback?.trim();
    if (trimmed) body.feedback = trimmed;
    return unwrap(() => erpatClient.appRating.create(body)) as Promise<AppRatingResult>;
  },
};

/** Read an unread-count payload that may be a bare number or `{ count }`. */
function readCount(data: unknown): number {
  if (typeof data === 'number') return data;
  if (data && typeof data === 'object') {
    const r = data as Json;
    const v = r.count ?? r.unread ?? r.unread_count ?? r.total;
    if (typeof v === 'number') return v;
  }
  return 0;
}

// ── Mood Tracker (the "Kamusta ka?" Home widget) ──────────────────────────
/**
 * Self-scoped: the caller's own mood only. 403/404 means the module is off for
 * this tenant — the widget latches `moodDenied` and hides for the session.
 *
 * POST error contract, verified against modules/Wellness/controllers/Wellness_mood_euapi.php
 * (renamed from modules/MoodTracker/.../Mood_tracker_euapi.php in Wellness 1.2.0):
 *   • 409 `debounced`  — a DIFFERENT mood inside the cooldown. `details.retry_after_seconds`
 *                        says how long to wait. NOT 429: that belongs to the rate-limit
 *                        middleware and carries its own telemetry.
 *   • 200 `already_logged` — the SAME mood re-sent inside the cooldown. A SUCCESS, not an
 *                        error: the tap is the user's intent already recorded.
 *   • 422 validation_failed with `details.fields.option_id` — the option was retired under
 *                        the client; re-fetch today() and re-render the buttons.
 *
 * The server owns the cooldown. Never enforce it client-side as the source of truth —
 * mirror `retry_after_seconds` instead.
 */
export const moodService = {
  today: (signal?: AbortSignal): Promise<MoodTodayVM> =>
    unwrap(() => erpatClient.request('/v1/api/me/mood', { signal })).then(mapMoodToday),

  /** Body keys are the server's snake_case names — house rule. */
  log: (optionId: string, source: 'widget' | 'sheet' = 'widget', note?: string): Promise<Json> =>
    unwrap(() =>
      erpatClient.request('/v1/api/me/mood', {
        method: 'POST',
        body: { option_id: Number(optionId), source, ...(note ? { note } : {}) },
      }),
    ) as Promise<Json>,

  history: (
    query?: { from?: string; to?: string; page?: number; per_page?: number },
    signal?: AbortSignal,
  ): Promise<{ entries: MoodEntryVM[]; total: number }> => {
    const qs = new URLSearchParams();
    if (query?.from) qs.set('from', query.from);
    if (query?.to) qs.set('to', query.to);
    if (query?.page) qs.set('page', String(query.page));
    if (query?.per_page) qs.set('per_page', String(query.per_page));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return unwrap(() => erpatClient.request(`/v1/api/me/mood/history${suffix}`, { signal })).then(
      mapMoodHistory,
    );
  },
};

/* ------------------------------------------------------------------ *
 * Todo comments — task conversations (Todo module 1.13.0)
 * ------------------------------------------------------------------ */

/** One attachment on a comment. Both URLs are bearer-protected API paths. */
export interface TodoCommentFileVM {
  name: string;
  extension: string;
  size: number;
  isImage: boolean;
  /**
   * A servable URL, resolved server-side by the app's storage layer — so it is
   * an S3 URL on an S3 tenant and a `/storage/...` path otherwise. Images are
   * already downscaled at upload by that same layer, so this is what the thread
   * renders directly.
   */
  url: string;
}

export interface TodoCommentReactionVM {
  code: string;
  emoji: string;
  count: number;
  /** Whether the signed-in reader is one of them. */
  mine: boolean;
  who: string[];
}

export interface TodoCommentVM {
  id: string;
  /** '' for a root comment; the answered comment's id for a reply. */
  parentId: string;
  description: string;
  author: string;
  authorId: string;
  avatarUrl: string;
  createdAtUtc: string;
  isPinned: boolean;
  isMine: boolean;
  canDelete: boolean;
  /** User ids the server matched, so the app highlights the same names. */
  mentions: string[];
  quoted: { author: string; excerpt: string } | null;
  files: TodoCommentFileVM[];
  reactions: TodoCommentReactionVM[];
}

export interface TodoMentionableVM {
  id: string;
  name: string;
  avatarUrl: string;
}

export interface TodoCommentThreadVM {
  comments: TodoCommentVM[];
  /** Sent with the thread so the picker cannot drift from what the server enforces. */
  reactionsAllowed: { code: string; emoji: string }[];
  /** Who an `@` may name on this task — nobody else resolves. */
  mentionables: TodoMentionableVM[];
}

/**
 * Resolve an attachment URL — the same rule the message attachments use.
 *
 * ABSOLUTE URLs pass through untouched: on an S3 tenant the storage layer
 * returns a URL on the bucket's own host, and rewriting that to this API's
 * origin would point every image at a path that does not exist. Only a
 * relative path is resolved, against the API origin.
 */
function apiFileUrl(value: unknown): string {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return '';
  if (/^(https?:|data:)/i.test(raw)) return raw;
  try {
    const origin = new URL(API_BASE_URL).origin;
    return new URL(raw.startsWith('/') ? raw : `/${raw}`, origin).toString();
  } catch {
    return '';
  }
}

function mapCommentFile(raw: unknown): TodoCommentFileVM {
  const r = asRecord(raw);
  return {
    name: typeof r.name === 'string' ? r.name : 'Attachment',
    extension: typeof r.extension === 'string' ? r.extension : '',
    size: Math.max(0, Number(r.size) || 0),
    isImage: r.is_image === true,
    url: apiFileUrl(r.url),
  };
}

function mapCommentReaction(raw: unknown): TodoCommentReactionVM {
  const r = asRecord(raw);
  return {
    code: typeof r.code === 'string' ? r.code : '',
    emoji: typeof r.emoji === 'string' ? r.emoji : '',
    count: Math.max(0, Number(r.count) || 0),
    mine: r.mine === true,
    who: toList<unknown>(r.who).map((w) => String(w)),
  };
}

function mapComment(raw: unknown): TodoCommentVM {
  const r = asRecord(raw);
  const quoted = asRecord(r.quoted);
  const hasQuote = typeof quoted.author === 'string' || typeof quoted.excerpt === 'string';
  return {
    id: String(r.id ?? ''),
    parentId: Number(r.parent_id) > 0 ? String(r.parent_id) : '',
    description: typeof r.description === 'string' ? r.description : '',
    author: typeof r.author === 'string' ? r.author : '',
    authorId: String(r.author_id ?? ''),
    avatarUrl: typeof r.avatar_url === 'string' ? r.avatar_url : '',
    createdAtUtc: typeof r.created_at_utc === 'string' ? r.created_at_utc : '',
    isPinned: r.is_pinned === true,
    isMine: r.is_mine === true,
    canDelete: r.can_delete === true,
    mentions: toList<unknown>(r.mentions).map((m) => String(m)),
    quoted: hasQuote
      ? {
          author: typeof quoted.author === 'string' ? quoted.author : '',
          excerpt: typeof quoted.excerpt === 'string' ? quoted.excerpt : '',
        }
      : null,
    files: toList<unknown>(r.files).map(mapCommentFile),
    reactions: toList<unknown>(r.reactions).map(mapCommentReaction),
  };
}

function mapCommentThread(data: unknown): TodoCommentThreadVM {
  const body = asRecord(domainBody(data, ['comments']));
  return {
    comments: toList<unknown>(body.comments).map(mapComment),
    reactionsAllowed: toList<unknown>(body.reactions_allowed).map((raw) => {
      const r = asRecord(raw);
      return {
        code: typeof r.code === 'string' ? r.code : '',
        emoji: typeof r.emoji === 'string' ? r.emoji : '',
      };
    }),
    mentionables: toList<unknown>(body.mentionables).map((raw) => {
      const r = asRecord(raw);
      return {
        id: String(r.id ?? ''),
        name: typeof r.name === 'string' ? r.name : '',
        avatarUrl: typeof r.avatar_url === 'string' ? r.avatar_url : '',
      };
    }),
  };
}

/**
 * Task conversations, mirroring ERPat's web comment drawer over JSON.
 *
 * Reached through `erpatClient.request()` for the same reason the focus service
 * is: @erpat/sdk has no namespace for these yet.
 */
export const todoCommentsService = {
  /** The thread, and reading it clears THIS reader's unread badge server-side. */
  thread: (todoId: string, signal?: AbortSignal): Promise<TodoCommentThreadVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/todos/${encodeURIComponent(todoId)}/comments`, { signal }),
    ).then(mapCommentThread),

  /** Post a comment, or a reply when `parentId` is given. */
  post: (
    todoId: string,
    input: { description: string; parentId?: string; uploadTokens?: string[] },
  ): Promise<TodoCommentVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/todos/${encodeURIComponent(todoId)}/comments`, {
        method: 'POST',
        body: {
          description: input.description,
          ...(input.parentId ? { parent_id: Number(input.parentId) } : {}),
          ...(input.uploadTokens?.length ? { upload_tokens: input.uploadTokens } : {}),
        },
      }),
    ).then((data) => mapComment(domainBody(data, ['comment']))),

  remove: (commentId: string): Promise<unknown> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/todos/comments/${encodeURIComponent(commentId)}`, {
        method: 'DELETE',
      }),
    ),

  /** Toggle one of YOUR reactions; returns the comment's reactions afterwards. */
  react: (commentId: string, reaction: string): Promise<TodoCommentReactionVM[]> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/todos/comments/${encodeURIComponent(commentId)}/react`, {
        method: 'POST',
        body: { reaction },
      }),
    ).then((data) => toList<unknown>(asRecord(domainBody(data, ['reactions'])).reactions).map(mapCommentReaction)),

  pin: (commentId: string): Promise<boolean> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/todos/comments/${encodeURIComponent(commentId)}/pin`, {
        method: 'POST',
      }),
    ).then((data) => asRecord(domainBody(data, ['is_pinned'])).is_pinned === true),

  /** Stage an attachment; the token is submitted with the comment. */
  upload: (file: File): Promise<MessageUploadResult> =>
    uploadMessageAttachment('/v1/api/me/todos/comments/uploads', file),
};

// ── Assets (Safekeep) ────────────────────────────────────────────────────────
/**
 * The signed-in employee's endorsed equipment, served by ERPat's
 * `modules/Assets/` End-User API (`controllers/Assets_euapi.php` +
 * `Asset_categories_euapi.php`). Not in `@erpat/sdk`, so every call goes through
 * the `request()` escape hatch:
 *   GET  /v1/api/me/asset-categories                 — type catalog + field schemas
 *   GET  /v1/api/me/assets                           — MY endorsed assets
 *   GET  /v1/api/me/assets/{id}                      — one asset, in full
 *   POST /v1/api/me/assets/{id}/acknowledge          — confirm receipt (idempotent)
 *   POST /v1/api/me/assets/{id}/return-request       — ask to return (idempotent)
 *   POST /v1/api/me/assets/{id}/issues               — report a defect (201)
 *   GET  /v1/api/me/assets/{id}/issues               — that asset's fault history
 *   POST /v1/api/me/assets/{id}/issue-verification   — confirm / reject a repair
 *   GET  /v1/api/me/asset-endorsements               — handover documents
 *   GET  /v1/api/me/asset-endorsements/{id}
 *   POST /v1/api/me/asset-endorsements/{id}/accept
 *   POST /v1/api/me/asset-endorsements/{id}/reject
 *   GET  /v1/api/me/caretaker-assets                 — kit held for a branch/dept
 *
 * Gated ONLY on the tenant's assets module (`requireModule('module_assets')`) —
 * there is NO permit on this surface, so nothing needs mirroring client-side. A
 * disabled module answers 404 `module_unavailable`, and an id that is not the
 * caller's answers 404 as well (never 403, to avoid leaking existence), so
 * useAsyncData maps both to "unavailable".
 *
 * ⚠ The endorsement list answers under the `endorsements` domain key and the
 * caretaker list under `assets` — neither matches its OpenAPI schema name
 * (`EuapiAssetEndorsementRow` / `EuapiCaretakerAsset`). `toList` finds the array
 * either way, but do not "correct" the keys.
 */

/** `/v1/api/me/assets` query. `view` defaults to `current` server-side. */
export interface AssetListQuery {
  /** current = still in custody · history = returned. */
  view?: 'current' | 'history';
  category_id?: number;
  status?: string;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface AssetIssueListQuery {
  status?: string;
  /** `1` to return only issues that are still open. */
  open_only?: 1;
  page?: number;
  per_page?: number;
}

export interface AssetEndorsementListQuery {
  /** `1` to return only documents still awaiting acceptance. */
  pending_only?: 1;
  status?: string;
  page?: number;
  per_page?: number;
}

/** `/v1/api/me/assets`: "per_page (max 100)" — the ceiling to page at. */
const ASSETS_MAX_PAGE_SIZE = 100;
/** Safety stop for the paging walk — far past any real employee's asset count. */
const ASSETS_MAX_PAGES = 10;

/** Verification outcome for `POST /me/assets/{id}/issue-verification`. */
export type IssueVerificationOutcome = 'verified' | 'failed';

export const assetsService = {
  /**
   * The org-wide asset type catalog, with each type's compiled field schema.
   *
   * Deliberately NOT cached across the session. The detail page needs a
   * category's `field_schema` to render its Details rows, and a module-level
   * cache would have to be invalidated on a tenant switch to stay honest — a
   * second small request per detail open is the cheaper correctness.
   */
  categories: (signal?: AbortSignal): Promise<AssetCategoryVM[]> =>
    unwrap(() => erpatClient.request('/v1/api/me/asset-categories', { signal })).then(mapAssetCategories),

  /** ONE page of assets — the server's default of 20 unless `per_page` says otherwise. */
  list: async (query?: AssetListQuery, signal?: AbortSignal): Promise<Paged<AssetVM>> => {
    const result = await unwrapList<Json>(() =>
      erpatClient.request('/v1/api/me/assets', {
        query: query as Record<string, QueryValue> | undefined,
        signal,
      }),
    );
    return { items: mapAssets(result.items), pagination: result.pagination };
  },

  /**
   * EVERY asset in one view, walked page by page.
   *
   * The list page derives its segment counts, its search box and its group
   * filter chips from the full set client-side — the way the module has always
   * behaved — and the server offers no "all views" mode, so each segment is
   * fetched whole. An employee's own custody is small; in practice this is one
   * request per view.
   *
   * Rows are collected raw and mapped ONCE at the end: `mapAssets` falls back to
   * the row INDEX for an id-less row, so mapping page by page would restart that
   * counter and collide ids across pages.
   */
  listAll: async (view: 'current' | 'history', signal?: AbortSignal): Promise<AssetVM[]> => {
    const rows: Json[] = [];

    for (let page = 1; page <= ASSETS_MAX_PAGES; page += 1) {
      const result = await unwrapList<Json>(() =>
        erpatClient.request('/v1/api/me/assets', {
          query: { view, page, per_page: ASSETS_MAX_PAGE_SIZE } as Record<string, QueryValue>,
          signal,
        }),
      );
      if (!result.items.length) break;
      rows.push(...result.items);

      // The server echoes the page it served. If that is not the page we asked
      // for — it ignored the param, or sent no meta — asking again would re-fetch
      // the same rows, so stop with what we have.
      if (result.pagination.page !== page) break;
      if (result.pagination.total > 0 && rows.length >= result.pagination.total) break;
      const served = result.pagination.perPage > 0 ? result.pagination.perPage : ASSETS_MAX_PAGE_SIZE;
      if (result.items.length < served) break;
    }

    return mapAssets(rows);
  },

  /**
   * One asset in full. `fields` is that asset's category schema, which the
   * caller looks up from {@link assetsService.categories} — the detail payload
   * embeds only the category REFERENCE, never its schema.
   */
  get: (id: string, fields: AssetFieldSchemaField[] = [], signal?: AbortSignal): Promise<AssetDetailVM> =>
    unwrap(() => erpatClient.request(`/v1/api/me/assets/${encodeURIComponent(id)}`, { signal })).then((d) =>
      mapAssetDetail(d, fields),
    ),

  /** Confirm receipt. Idempotent — a second call is a harmless no-op 200. */
  acknowledge: (id: string, note?: string): Promise<Json> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/assets/${encodeURIComponent(id)}/acknowledge`, {
        method: 'POST',
        body: note ? { note } : {},
      }),
    ) as Promise<Json>,

  /** Ask for the asset to be collected. Idempotent. */
  requestReturn: (id: string, note?: string): Promise<Json> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/assets/${encodeURIComponent(id)}/return-request`, {
        method: 'POST',
        body: note ? { note } : {},
      }),
    ) as Promise<Json>,

  /**
   * Report a defect. Answers **201** with the accepted issue.
   *
   * Mapped (unlike the other writes) because the caller must tell the user what
   * actually happened: `ticket_reference` is present only when a Helpdesk ticket
   * was raised, and `ticket_id` is null whenever Helpdesk is unavailable or the
   * severity did not warrant one.
   */
  reportIssue: (id: string, body: ReportIssueInput): Promise<ReportIssueResultVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/assets/${encodeURIComponent(id)}/issues`, {
        method: 'POST',
        body: { category: body.category, description: body.description.trim(), severity: body.severity },
      }),
    ).then(mapReportIssueResult),

  /** That asset's fault history, newest first. */
  issues: async (
    id: string,
    query?: AssetIssueListQuery,
    signal?: AbortSignal,
  ): Promise<Paged<AssetIssueVM>> => {
    const result = await unwrapList<Json>(() =>
      erpatClient.request(`/v1/api/me/assets/${encodeURIComponent(id)}/issues`, {
        query: query as Record<string, QueryValue> | undefined,
        signal,
      }),
    );
    return { items: mapAssetIssues(result.items), pagination: result.pagination };
  },

  /**
   * The holder's verdict on a repair. `failed` reopens the issue, and its `note`
   * is what the technician reads next — worth insisting on in the UI.
   *
   * Answers 409 `issue_not_verifiable` unless the issue is sitting at
   * `resolved_pending_verification`.
   */
  verifyIssue: (
    id: string,
    issueId: string,
    outcome: IssueVerificationOutcome,
    note?: string,
  ): Promise<Json> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/assets/${encodeURIComponent(id)}/issue-verification`, {
        method: 'POST',
        // `issue_id` is the asset_issues row — NOT the ledger event id the
        // report echoed back as `id`.
        body: { issue_id: Number(issueId), outcome, ...(note ? { note } : {}) },
      }),
    ) as Promise<Json>,

  /** Handover documents the caller is a party to (recipient, caretaker or owner). */
  endorsements: async (
    query?: AssetEndorsementListQuery,
    signal?: AbortSignal,
  ): Promise<Paged<AssetEndorsementVM>> => {
    const result = await unwrapList<Json>(() =>
      erpatClient.request('/v1/api/me/asset-endorsements', {
        query: query as Record<string, QueryValue> | undefined,
        signal,
      }),
    );
    return { items: mapEndorsements(result.items), pagination: result.pagination };
  },

  endorsement: (id: string, signal?: AbortSignal): Promise<AssetEndorsementDetailVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/asset-endorsements/${encodeURIComponent(id)}`, { signal }),
    ).then(mapEndorsementDetail),

  /**
   * Accept a handover. Idempotent.
   *
   * `signature_name` is compulsory when the document's `terms.requires_signature`
   * is true — the server answers 422 without it. Omitting `custodyIds` accepts
   * the whole document; passing them accepts only those items.
   */
  acceptEndorsement: (id: string, input: AcceptEndorsementInput = {}): Promise<Json> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/asset-endorsements/${encodeURIComponent(id)}/accept`, {
        method: 'POST',
        body: {
          ...(input.signatureName ? { signature_name: input.signatureName.trim() } : {}),
          ...(input.acceptTerms ? { accept_terms: true } : {}),
          ...(input.note ? { note: input.note.trim() } : {}),
          ...(input.custodyIds?.length ? { custody_ids: input.custodyIds.map(Number) } : {}),
        },
      }),
    ) as Promise<Json>,

  /** Refuse a handover. `reason` is required by the server. */
  rejectEndorsement: (id: string, reason: string, custodyIds?: string[]): Promise<Json> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/asset-endorsements/${encodeURIComponent(id)}/reject`, {
        method: 'POST',
        body: {
          reason: reason.trim(),
          ...(custodyIds?.length ? { custody_ids: custodyIds.map(Number) } : {}),
        },
      }),
    ) as Promise<Json>,

  /**
   * Equipment the caller looks after on behalf of a branch, department, project
   * or room.
   *
   * ⚠ These rows are NOT reachable through `get(id)`: that endpoint self-scopes
   * on the legacy `issued_to` projection and a caretaker is never the custody
   * target, so a detail fetch would answer 404. Everything shown for a caretaker
   * asset must come from this row.
   */
  caretakerAssets: async (
    query?: { page?: number; per_page?: number },
    signal?: AbortSignal,
  ): Promise<Paged<CaretakerAssetVM>> => {
    const result = await unwrapList<Json>(() =>
      erpatClient.request('/v1/api/me/caretaker-assets', {
        query: query as Record<string, QueryValue> | undefined,
        signal,
      }),
    );
    return { items: mapCaretakerAssets(result.items), pagination: result.pagination };
  },
};

// ── Projects ─────────────────────────────────────────────────────────────────
/**
 * The signed-in employee's projects, project tasks and project time, served by
 * ERPat's `modules/Projects/` End-User API. Not in `@erpat/sdk`, so every call
 * goes through the `request()` escape hatch:
 *
 *   GET   /v1/api/me/projects                    — projects I am a MEMBER of
 *   GET   /v1/api/me/projects/{id}               — one project, in full
 *   GET   /v1/api/me/projects/{id}/tasks         — that project's tasks
 *   GET   /v1/api/me/projects/{id}/milestones    — its milestones
 *   GET   /v1/api/me/projects/{id}/members       — its team
 *   GET   /v1/api/me/projects/{id}/comments      — its discussion
 *   POST  /v1/api/me/projects/{id}/star          — star / unstar (personal)
 *   GET   /v1/api/me/tasks                       — MY tasks across every project
 *   GET   /v1/api/me/tasks/statuses              — the tenant's board columns
 *   GET   /v1/api/me/tasks/{id}                  — one task (+ checklist)
 *   GET   /v1/api/me/tasks/{id}/comments         — its thread
 *   POST  /v1/api/me/tasks/{id}/comments         — post a comment (201)
 *   PATCH /v1/api/me/tasks/{id}/status           — move it to another column
 *   PATCH /v1/api/me/tasks/checklist/{id}        — tick / untick an item
 *   GET   /v1/api/me/time-logs                   — my completed time
 *   GET   /v1/api/me/timers                      — my RUNNING timers
 *   POST  /v1/api/me/timers                      — start one (idempotent)
 *   POST  /v1/api/me/timers/{id}/stop            — stop one, by row id
 *
 * GATING. Reads need only the tenant's `module_projects`; writes are staff-only
 * and carry the web's permission rules. A disabled module answers 404
 * `module_unavailable`, and anything outside the caller's membership answers 404
 * too — never 403, so existence is never leaked — which `useAsyncData` maps to
 * "unavailable" either way.
 *
 * NAMING. Deliberately `projectTasksService`, not `tasksService`: `todosService`
 * on this same object is the PERSONAL to-do list, a different feature entirely,
 * and one letter of difference between the two would be a standing trap.
 *
 * DEPLOY SKEW. The write endpoints shipped after the reads. `projectTimeService
 * .timers()` is therefore also the app's capability probe — it 404s only when
 * the module is genuinely off, and reports `canManage` for whether this caller
 * may write at all, so the app can hide actions rather than offer ones that fail.
 */

/** `per_page` ceiling the API enforces. */
const PROJECTS_MAX_PAGE_SIZE = 100;

/**
 * Caps on the Milestones fan-out (`projectsService.milestoneBoard`).
 *
 * There is no cross-project milestones endpoint, so that page costs one request
 * per project. These two numbers are what stop a heavily-staffed account from
 * turning a page open into a burst of requests over mobile data.
 */
const MILESTONE_FANOUT_CONCURRENCY = 4;
const MILESTONE_FANOUT_MAX_PROJECTS = 40;

export interface ProjectListQuery {
  status?: ProjectStatus | string;
  label?: number;
  starred?: boolean;
  /** 'expired', or a YYYY-MM-DD (projects due on/before it). */
  deadline?: string;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface ProjectTaskListQuery {
  project_id?: number;
  status_id?: number;
  milestone_id?: number;
  label?: number;
  /** 'expired', or a YYYY-MM-DD. */
  deadline?: string;
  overdue?: boolean;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface ProjectTimeLogQuery {
  project_id?: number;
  task_id?: number;
  start_date?: string;
  end_date?: string;
  page?: number;
  per_page?: number;
}

export const projectsService = {
  list: async (query?: ProjectListQuery, signal?: AbortSignal): Promise<Paged<ProjectVM>> => {
    const result = await unwrapList<Json>(() =>
      erpatClient.request('/v1/api/me/projects', {
        query: query as Record<string, QueryValue> | undefined,
        signal,
      }),
    );
    return { items: mapProjects(result.items), pagination: result.pagination };
  },

  get: (id: string, signal?: AbortSignal): Promise<ProjectDetailVM> =>
    unwrap(() => erpatClient.request(`/v1/api/me/projects/${encodeURIComponent(id)}`, { signal }))
      .then(mapProjectDetail),

  tasks: async (
    id: string,
    query?: ProjectTaskListQuery,
    signal?: AbortSignal,
  ): Promise<Paged<ProjectTaskVM>> => {
    const result = await unwrapList<Json>(() =>
      erpatClient.request(`/v1/api/me/projects/${encodeURIComponent(id)}/tasks`, {
        query: query as Record<string, QueryValue> | undefined,
        signal,
      }),
    );
    return { items: mapProjectTasks(result.items), pagination: result.pagination };
  },

  // These three are NOT paginated by the API — one request returns the lot.
  milestones: (id: string, signal?: AbortSignal): Promise<ProjectMilestoneVM[]> =>
    unwrap(() => erpatClient.request(`/v1/api/me/projects/${encodeURIComponent(id)}/milestones`, { signal }))
      .then(mapProjectMilestones),

  members: (id: string, signal?: AbortSignal): Promise<ProjectMemberVM[]> =>
    unwrap(() => erpatClient.request(`/v1/api/me/projects/${encodeURIComponent(id)}/members`, { signal }))
      .then(mapProjectMembers),

  comments: (id: string, signal?: AbortSignal): Promise<ProjectCommentVM[]> =>
    unwrap(() => erpatClient.request(`/v1/api/me/projects/${encodeURIComponent(id)}/comments`, { signal }))
      .then(mapProjectComments),

  /** Star / unstar. Returns the server's view of the flag, not the requested one. */
  setStarred: (id: string, starred: boolean): Promise<boolean> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/projects/${encodeURIComponent(id)}/star`, {
        method: 'POST',
        body: { starred },
      }),
    ).then((d) => Boolean(asRecord(d as Json).is_starred)),

  /**
   * One project plus the sub-resources its detail page shows.
   *
   * ONLY the project leg may reject: `useAsyncData` surfaces a single
   * `errorStatus`, so a 404 from a sub-resource would masquerade as the
   * project's own membership 404 and show the whole page as unavailable. The
   * rest degrade to null and their tab renders an empty state.
   */
  detail: async (
    id: string,
    signal?: AbortSignal,
  ): Promise<{
    project: ProjectDetailVM;
    milestones: ProjectMilestoneVM[] | null;
    members: ProjectMemberVM[] | null;
    comments: ProjectCommentVM[] | null;
  }> => {
    const [project, milestones, members, comments] = await Promise.all([
      projectsService.get(id, signal),
      projectsService.milestones(id, signal).catch(() => null),
      projectsService.members(id, signal).catch(() => null),
      projectsService.comments(id, signal).catch(() => null),
    ]);
    return { project, milestones, members, comments };
  },

  /**
   * Every milestone across the caller's projects — the Milestones page's data.
   *
   * There is NO cross-project milestones endpoint (see the route list above), so
   * this fans out: one `/projects` page, then one `/milestones` call per project.
   * Three properties keep that honest rather than merely convenient:
   *
   *  - **Bounded.** At most `MILESTONE_FANOUT_MAX_PROJECTS` projects are queried,
   *    `MILESTONE_FANOUT_CONCURRENCY` at a time, so a member of eighty projects
   *    cannot open eighty sockets from a phone. When the cap bites,
   *    `truncated` is set and the page says so — it does not silently show a
   *    partial plan as if it were the whole one.
   *  - **Only the PROJECTS leg may reject.** `useAsyncData` surfaces a single
   *    `errorStatus`, so a 404 from one project's milestones would masquerade as
   *    the module being unavailable and blank the page. A failed leg degrades to
   *    nothing and is counted in `failedProjects` instead.
   *  - **Projects come back too.** The rows need each project's title, and the
   *    Gantt needs its `start_date` to anchor the first bar.
   */
  milestoneBoard: async (
    signal?: AbortSignal,
  ): Promise<{
    projects: ProjectVM[];
    milestones: ProjectMilestoneVM[];
    /** Projects whose milestones could not be loaded. */
    failedProjects: number;
    /** True when more projects exist than the fan-out cap allows. */
    truncated: boolean;
  }> => {
    const { items: projects, pagination } = await projectsService.list(
      { per_page: PROJECTS_MAX_PAGE_SIZE },
      signal,
    );

    const queried = projects.slice(0, MILESTONE_FANOUT_MAX_PROJECTS);
    const milestones: ProjectMilestoneVM[] = [];
    let failedProjects = 0;

    // A simple index-stealing pool: N workers share one cursor, so a slow
    // project never blocks the others and the number in flight never exceeds N.
    let cursor = 0;
    const worker = async (): Promise<void> => {
      for (;;) {
        const index = cursor++;
        const project = queried[index];
        if (!project) return;
        try {
          const rows = await projectsService.milestones(project.id, signal);
          milestones.push(...rows);
        } catch {
          // An aborted load is about to be discarded wholesale by the caller,
          // so counting it as a failure would only flash a stale warning.
          if (signal?.aborted) return;
          failedProjects += 1;
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(MILESTONE_FANOUT_CONCURRENCY, queried.length) }, worker),
    );

    return {
      projects,
      milestones,
      failedProjects,
      truncated: pagination.total > queried.length,
    };
  },
};

export const projectTasksService = {
  /** Every task assigned to or shared with the caller, across all projects — the My Tasks list. */
  list: async (query?: ProjectTaskListQuery, signal?: AbortSignal): Promise<Paged<ProjectTaskVM>> => {
    const result = await unwrapList<Json>(() =>
      erpatClient.request('/v1/api/me/tasks', {
        query: query as Record<string, QueryValue> | undefined,
        signal,
      }),
    );
    return { items: mapProjectTasks(result.items), pagination: result.pagination };
  },

  /** The tenant's board columns. Unpaginated, and stable enough to cache per view. */
  statuses: (signal?: AbortSignal): Promise<ProjectTaskStatusVM[]> =>
    unwrap(() => erpatClient.request('/v1/api/me/tasks/statuses', { signal })).then(mapProjectTaskStatuses),

  get: (id: string, signal?: AbortSignal): Promise<ProjectTaskDetailVM> =>
    unwrap(() => erpatClient.request(`/v1/api/me/tasks/${encodeURIComponent(id)}`, { signal }))
      .then(mapProjectTaskDetail),

  comments: (id: string, signal?: AbortSignal): Promise<ProjectCommentVM[]> =>
    unwrap(() => erpatClient.request(`/v1/api/me/tasks/${encodeURIComponent(id)}/comments`, { signal }))
      .then(mapProjectComments),

  /** Move the task to another board column. Returns the re-read task. */
  setStatus: (id: string, statusId: string): Promise<ProjectTaskVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/tasks/${encodeURIComponent(id)}/status`, {
        method: 'PATCH',
        body: { status_id: Number(statusId) },
      }),
    ).then(mapProjectTask),

  /** Post a top-level comment. The API accepts `body`, not `message`. */
  addComment: (id: string, body: string): Promise<ProjectCommentVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/tasks/${encodeURIComponent(id)}/comments`, {
        method: 'POST',
        body: { body },
      }),
    ).then(mapProjectComment),

  /** Tick / untick a checklist item. The path addresses the ITEM, not the task. */
  setChecklistItem: (itemId: string, isChecked: boolean): Promise<ProjectChecklistItemVM> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/tasks/checklist/${encodeURIComponent(itemId)}`, {
        method: 'PATCH',
        body: { is_checked: isChecked },
      }),
    ).then(mapProjectChecklistItem),
};

export const projectTimeService = {
  logs: async (query?: ProjectTimeLogQuery, signal?: AbortSignal): Promise<Paged<ProjectTimeLogVM>> => {
    const result = await unwrapList<Json>(() =>
      erpatClient.request('/v1/api/me/time-logs', {
        query: query as Record<string, QueryValue> | undefined,
        signal,
      }),
    );
    return { items: mapProjectTimeLogs(result.items), pagination: result.pagination };
  },

  /**
   * Running timers + this caller's capabilities.
   *
   * `fetchedAtMs` is stamped from the response so the UI can count up locally
   * without ever parsing `started_at` (a tenant-local wall clock with no offset).
   */
  timers: (signal?: AbortSignal): Promise<ProjectTimersVM> =>
    unwrap(() => erpatClient.request('/v1/api/me/timers', { signal }))
      .then((d) => mapProjectTimers(d, Date.now())),

  /** Start a timer. Idempotent server-side — a repeat returns the running one. */
  start: (projectId: string, taskId?: string, note?: string): Promise<ProjectTimerVM> =>
    unwrap(() =>
      erpatClient.request('/v1/api/me/timers', {
        method: 'POST',
        body: {
          project_id: Number(projectId),
          ...(taskId ? { task_id: Number(taskId) } : {}),
          ...(note ? { note } : {}),
        },
      }),
    ).then((d) => mapProjectTimer(d, Date.now())),

  /**
   * Stop one timer by its row id.
   *
   * Omitting `note` leaves whatever the timer was started with — sending an
   * empty string would blank it — so the key is only included when there is
   * something to say.
   */
  stop: (timerId: string, note?: string): Promise<Json> =>
    unwrap(() =>
      erpatClient.request(`/v1/api/me/timers/${encodeURIComponent(timerId)}/stop`, {
        method: 'POST',
        body: note ? { note } : {},
      }),
    ) as Promise<Json>,
};

export { PROJECTS_MAX_PAGE_SIZE };
